/* aviaf — mobile dashboard */
/* CR/Alerts/Ops mobile console — bundled into THIS (code-split) mobile chunk so
 * desktop never downloads it.  ops-css injects the scoped mobile design delta;
 * mobile-ops defines window.MScreen{Alerts2,Ops,Research} + MCritStrip +
 * MIncidentSheet on the real window.OpsStore.  Both run before mobile.jsx's
 * body but only USE its exports (MSpark, …) at render time — safe. */
import "./mobile-ops-css.jsx";
import "./mobile-ops.jsx";
const { useState: mState, useEffect: mEffect, useRef: mRef, useMemo: mMemo, useCallback: mCB } = React;

/* ───── Per-source loading subscription ───────────────────────
 * Mobile mirrors desktop's per-source shimmer: each card shimmers
 * until its specific /api/* has resolved.  We poll __LIVE.rev at
 * 250ms (matching ui.jsx useDataLoad) so flag flips dissolve
 * shimmer responsively without waiting on the 1Hz app tick.
 */
function useMLoaded() {
  // Re-render on live-data bumps via useSyncExternalStore (window.useLive)
  // instead of polling __LIVE.rev every 250ms — shimmer flips dissolve
  // the instant the source resolves, with no idle timer.
  window.useLive();
  const flags = (window.__LIVE && window.__LIVE.loaded) || {};
  const loading = (k) => {
    if (k === "strats") return !(window.__LIVE && window.__LIVE.strats_ready);
    return flags[k] !== true;
  };
  const anyLoading = !window.__LIVE?.hasData ||
    ["accounts","equitySnapshot","fills","equitySeries","feed","alerts"]
      .some((k) => flags[k] !== true) ||
    !(window.__LIVE && window.__LIVE.strats_ready);
  return { loading, anyLoading, flags };
}

/* Compact shimmer span for inline values (numbers, labels).
 * Width/height are explicit so the placeholder reserves the same
 * footprint as the eventual value — no layout shift on hydration. */
function MSkel({ w, h = 12, style }) {
  return (
    <span className="skel skel-line" style={{
      display: "inline-block",
      width: typeof w === "number" ? w + "px" : (w || "60%"),
      height: typeof h === "number" ? h + "px" : h,
      verticalAlign: "middle",
      ...(style || {}),
    }} />
  );
}

/* ───── Shared live-data selectors ─────────────────────────
 * Mirror desktop PageOverview's "ours-only" semantics: accounts on
 * read-only mirror instances (window.EXTERNAL_INSTANCES) are real
 * trades on real accounts but NOT aviaf deployments — they belong on
 * the account list yet must not inflate fleet KPIs / counts. */
function mIsExternalAcct(a) {
  return !!(a && window.EXTERNAL_INSTANCES && window.EXTERNAL_INSTANCES.has(a.instance));
}
function mOwnAccts() {
  return (window.ACCOUNTS || []).filter((a) => !mIsExternalAcct(a));
}
function mOwnStrats() {
  return (window.STRATS || []).filter((s) => !s.external);
}
/* window.ALERTS holds RAW DAL rows (severity/acknowledged), not the
 * normalized {sev, ack} shape — reading a.sev off raw rows silently
 * yielded 0 for every count (the overview callout permanently showed
 * "0 crit · 0 warn").  These helpers read both shapes. */
function mAlSev(a) {
  const v = String(a.sev || a.severity || "").toLowerCase();
  if (v === "crit" || v === "critical" || v === "alarm" || v === "error") return "crit";
  if (v === "warn" || v === "warning") return "warn";
  return "info";
}
function mAlAck(a) { return !!(a.ack || a.acknowledged); }

/* ───── Sparkline ─────────────────────────────────────────── */
function MSpark({ data, h = 28, color, fill }) {
  const w = 100;
  if (!data || data.length === 0) return null;
  const lo = Math.min(...data), hi = Math.max(...data);
  const span = hi - lo || 1;
  const pts = data.map((v, i) => [
    (i / (data.length - 1)) * w,
    h - 2 - ((v - lo) / span) * (h - 4),
  ]);
  const d = pts.map((p, i) => (i === 0 ? "M" : "L") + p[0].toFixed(1) + "," + p[1].toFixed(1)).join("");
  const c = color || "currentColor";
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: "100%", height: h, display: "block", color: c }}>
      {fill && <path d={`${d} L${w},${h} L0,${h} Z`} fill={c} opacity="0.10" />}
      <path d={d} fill="none" stroke={c} strokeWidth="1.2" />
    </svg>
  );
}

/* ───── State badge ───────────────────────────────────────── */
function MStateBadge({ state }) {
  const map = {
    running:  { c: "var(--pos)",  lbl: "LIVE" },
    armed:    { c: "var(--accent)", lbl: "ARMED" },
    paused:   { c: "var(--warn)", lbl: "PAUSED" },
    stopped:  { c: "var(--muted-2)", lbl: "STOPPED" },
    degraded: { c: "var(--warn)", lbl: "DEGRADED" },
    breached: { c: "var(--neg)",  lbl: "BREACH" },
  };
  const s = map[state] || map.stopped;
  return (
    <span className="m-state" style={{ color: s.c, borderColor: s.c }}>
      <span className="m-state-dot" style={{ background: s.c }} />
      {s.lbl}
    </span>
  );
}

/* ───── Header ────────────────────────────────────────────── */
/* Clock is its own component so its 1 Hz tick repaints ONE <span> — the
 * old MobileApp-owned `now` state re-rendered the entire app tree every
 * second (visible jank on phones, the main "glitchy" report). */
function MClock() {
  const [now, setNow] = mState(() => new Date());
  mEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const ct = now.toLocaleTimeString("en-US", { hour12: false, timeZone: "America/Chicago" }).slice(0, 5);
  return <span className="m-ct mono">{ct} <span className="muted">CT</span></span>;
}

/* Bell badge subscribes itself to live-data bumps; counts read the raw
 * alert rows directly (no polling, no root-owned alert list). */
function MBell({ openAlerts }) {
  window.useLive();
  const raw = window.ALERTS || [];
  const alertCount = raw.filter((a) => !mAlAck(a)).length;
  const critCount = raw.filter((a) => !mAlAck(a) && mAlSev(a) === "crit").length;
  return (
    <button className="m-icon-btn m-bell" aria-label="alerts" onClick={openAlerts}
      data-has-crit={critCount > 0}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M8 1.5v1M3 11V7a5 5 0 1110 0v4l1.4 1.5H1.6L3 11zm3 2a2 2 0 004 0" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      {alertCount > 0 && <span className="m-bell-badge">{alertCount > 9 ? "9+" : alertCount}</span>}
    </button>
  );
}

/* Global loading cue: pulses while a pull-refresh is in flight (forced)
 * or while any /api/* source hasn't landed yet. */
function MRefreshBar({ forced }) {
  const { anyLoading } = useMLoaded();
  return (forced || anyLoading) ? <div className="m-refresh-bar" /> : null;
}

function MHeader({ onMenu, refreshing, openAlerts }) {
  return (
    <header className="m-header">
      <button className="m-icon-btn" aria-label="menu" onClick={onMenu}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M2 5h16M2 10h16M2 15h12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square"/>
        </svg>
      </button>
      <div className="m-brand">
        <span className="m-brand-mark" />
        <span>aviaf</span>
        <span className="m-brand-sub">ops</span>
      </div>
      <div className="m-header-right">
        <MClock />
        <MBell openAlerts={openAlerts} />
      </div>
      <MRefreshBar forced={refreshing} />
    </header>
  );
}

/* ───── Status strip ───────────────────────────────────────── */
function MStatusStrip() {
  // All values are LIVE now.  The old strip hardcoded "ALL LIVE" and a
  // fake "BROKER 38ms" that read identically on a healthy hub and a dead
  // one; agent liveness + strategy counts are the real signals.
  const { loading } = useMLoaded();
  const acctLoading = loading("accounts");
  const stratLoading = loading("strats");
  const insts = window.INSTANCES || [];
  const liveInsts = insts.filter((i) => i.state === "running").length;
  const own = mOwnStrats();
  const deployed = own.filter((s) => s.state !== "stopped" && !s.retired);
  const running = deployed.filter((s) => s.state === "running" || s.state === "armed").length;
  const health = insts.length === 0 || liveInsts === 0 ? "neg" : liveInsts < insts.length ? "warn" : "ok";
  const word = health === "ok" ? "LIVE" : health === "warn" ? "PARTIAL" : "OFFLINE";
  return (
    <div className="m-status-strip mono">
      <span className={"m-pulse" + (health === "ok" ? "" : " warn")} />
      <span className={health === "neg" ? "neg" : health === "warn" ? "warn" : ""}>
        {acctLoading ? <MSkel w={56} h={10} /> : <>{word} · {liveInsts}/{insts.length} AGENT</>}
      </span>
      <span className="m-sep">·</span>
      <span>{acctLoading ? <MSkel w={48} h={10} /> : <>{mOwnAccts().length} ACCT</>}</span>
      <span className="m-sep">·</span>
      <span>{stratLoading ? <MSkel w={58} h={10} /> : <>{running}/{deployed.length} STRAT</>}</span>
      {(() => {
        const pc = window.__LIVE?.pingClientSrtt ?? window.__LIVE?.pingClient;
        const ps = window.__LIVE?.pingServerSrtt ?? window.__LIVE?.pingServer;
        if (pc == null && ps == null) return null;
        return (<>
          <span className="m-sep">·</span>
          <span title="WS round-trip · client / server">
            ⇅ <span style={{ display: "inline-block", minWidth: "5ch", textAlign: "right" }}>{pc == null ? "—" : pc + "ms"}</span>/<span style={{ display: "inline-block", minWidth: "5ch", textAlign: "right" }}>{ps == null ? "—" : ps + "ms"}</span>
          </span>
        </>);
      })()}
    </div>
  );
}

/* ───── Hero AUM ──────────────────────────────────────────── */
function MHero() {
  const { loading } = useMLoaded();
  // Ours-only + funded-aware, matching desktop PageOverview: when real-money
  // (xfa/funded) accounts exist the headline tracks only those; otherwise it
  // falls back to all own verified accounts so live equity is never hidden.
  // Externally-mirrored accounts never contribute (not aviaf's book).
  const own = mOwnAccts();
  const realAccts = own.filter((a) => a.kind === "xfa" || a.kind === "funded");
  const fundedMode = realAccts.length > 0;
  const aumAccts = fundedMode ? realAccts : own;
  const verified = aumAccts.filter((a) => a.equityVerified);
  const aum = verified.reduce((s, a) => s + (a.equity || 0), 0);
  const day = verified.reduce((s, a) => s + (a.dayPnl || 0), 0);
  const pos = day >= 0;
  const eqLoading = loading("equitySnapshot") || verified.length === 0;
  const seriesLoading = loading("equitySeries");
  const acctsLoading = loading("accounts");
  const series = (window.EQUITY_TOTAL || []).slice(-40);
  return (
    <section className="m-hero">
      <div className="m-hero-spark" style={{ color: "var(--accent)" }}>
        {seriesLoading || series.length < 2
          ? <div className="skel" style={{ height: 86, width: "100%" }} />
          : <MSpark data={series} h={86} fill />}
      </div>
      <div className="m-hero-body">
        <div className="m-hero-lbl mono">
          {fundedMode ? "AUM · XFA+FUNDED" : "TRACKED EQUITY"} · {acctsLoading ? <MSkel w={28} h={9} /> : <>{aumAccts.length} ACCT</>}
        </div>
        <div className="m-hero-num mono tnum">
          {eqLoading ? <MSkel w={140} h={22} /> : window.fmt$(aum, 0)}
        </div>
        <div className="m-hero-row">
          <span className={"m-hero-day mono tnum " + (pos ? "pos" : "neg")}>
            {eqLoading
              ? <MSkel w={70} h={12} />
              : <>{window.fmt$(day, 2)} <span className="muted">day</span></>}
          </span>
          <span className="m-hero-pct mono tnum muted">
            {eqLoading || aum === 0 ? <MSkel w={42} h={11} /> : window.pct(day / aum, 2)}
          </span>
        </div>
      </div>
    </section>
  );
}

/* ───── KPI strip (horizontal scroll) ─────────────────────── */
function MKpis() {
  const { loading } = useMLoaded();
  // Ours-only aggregates (desktop PageOverview parity): externally-mirrored
  // rows are excluded from every fleet KPI so "how is aviaf doing" never
  // silently becomes "aviaf plus somebody else's book".
  const strats = mOwnStrats();
  const own = mOwnAccts();
  const realAccts = own.filter((a) => a.kind === "xfa" || a.kind === "funded");
  const aumAccts = realAccts.length > 0 ? realAccts : own;
  const trades = strats.reduce((s, x) => s + window.safeN(x.trades, 0), 0);
  // Trade-weighted win rate (a trade-count-weighted mean of per-strategy win
  // rates IS the overall win rate).  Dash, not 0%, when there are no trades.
  const wrAvg = trades > 0
    ? strats.reduce((s, x) => s + (Number.isFinite(x.wr) ? x.wr : 0) * window.safeN(x.trades, 0), 0) / trades
    : null;
  // Fleet profit factor via the shared gross-win/gross-loss selector so
  // mobile matches desktop.  The old trade-weighted mean of per-strategy PF
  // ratios was mathematically wrong (read 0.86 vs the correct 1.23).
  const pfAvg = window.fleetProfitFactor(strats);
  // Worst 30d drawdown across strategies.  Was reading all-time s.dd under a
  // "30d worst" label — use the 30d slice to match the label.
  const ddMax = strats.length ? Math.max(0, ...strats.map(s => Math.abs(window.safeN(s.dd30, 0)))) : 0;
  const deployed = strats.filter(s => s.state !== "stopped" && !s.retired);
  const running = deployed.filter(s => s.state === "running" || s.state === "armed").length;
  const eqTotal = window.EQUITY_TOTAL || [];
  // 30D P&L = current verified AUM − per-account equity 30d ago, over the
  // same funded-aware ours-only account set desktop uses — the two surfaces
  // must never disagree on the headline number.
  const aum = aumAccts.reduce((s, a) => a.equityVerified ? s + (a.equity || 0) : s, 0);
  const aum30 = aumAccts.reduce((s, a) => s + (Number(a.eq30) || 0), 0);
  const pnl30Avail = aumAccts.some((a) => a.equityVerified);
  const pnl30 = pnl30Avail ? (aum - aum30) : 0;
  const has30Hist = aumAccts.some((a) => a.eq30HasHist);
  const lStrats = loading("strats");
  const items = [
    { lbl: "30D P&L",
      loading: loading("equitySnapshot"),
      val: pnl30Avail ? window.fmt$(pnl30, 0) : "—",
      sub: !pnl30Avail ? "no equity history" : has30Hist ? "trailing 30d" : "since inception",
      cls: pnl30 >= 0 ? "pos" : "neg",
      spark: eqTotal.slice(-40) },
    { lbl: "WIN RATE", loading: lStrats, val: (wrAvg != null && Number.isFinite(wrAvg)) ? (wrAvg * 100).toFixed(1) + "%" : "—", sub: trades + " trades" },
    { lbl: "PROFIT FCT", loading: lStrats, val: pfAvg == null ? "—" : (Number.isFinite(pfAvg) ? pfAvg.toFixed(2) : "∞"), sub: "gross win/loss" },
    { lbl: "LIVE", loading: lStrats, val: running + "/" + deployed.length, sub: "running·armed" },
    { lbl: "MAX DD", loading: lStrats, val: window.fmt$(-ddMax, 0), sub: "30d worst", cls: "neg" },
  ];
  return (
    <div className="m-kpis">
      {items.map((k, i) => (
        <div className="m-kpi" key={i}>
          <div className="m-kpi-lbl mono">{k.lbl}</div>
          <div className={"m-kpi-val mono tnum " + (k.cls || "")}>
            {k.loading ? <MSkel w={64} h={18} /> : k.val}
          </div>
          {k.spark
            ? <div className="m-kpi-spark" style={{ color: "var(--accent)" }}>
                {k.loading || k.spark.length < 2
                  ? <div className="skel" style={{ height: 20, width: "100%" }} />
                  : <MSpark data={k.spark} h={20} />}
              </div>
            : <div className="m-kpi-sub mono">
                {k.loading ? <MSkel w={48} h={9} /> : k.sub}
              </div>}
        </div>
      ))}
    </div>
  );
}

/* ───── Section header (mobile) ───────────────────────────── */
function MSection({ title, count, action, onAction, children }) {
  return (
    <section className="m-section">
      <header className="m-section-h">
        <h2 className="mono">
          {title}
          {count !== undefined && <span className="m-section-count mono"> {count}</span>}
        </h2>
        {action && (
          <button className="m-section-act mono" onClick={onAction}>{action}</button>
        )}
      </header>
      {children}
    </section>
  );
}

/* ───── Account card ──────────────────────────────────────── */
/* One compact status banner line (desktop AccountCard parity — mobile
 * previously dropped PRE-STAGED / HANDS-OFF / DLL / PAUSED entirely, so
 * a paused or manually-controlled account looked identical to a live
 * one on the phone). */
function MAcctBanner({ kind, children, title }) {
  return (
    <div className="m-acct-banner mono" data-kind={kind} title={title}>{children}</div>
  );
}

/* Compact P(pass)/T(pass) line for combines (agent pass_estimate emit —
 * hourly MC under full TopstepX rules).  Mirrors desktop PassEstRow. */
function MPassEst({ a }) {
  const pe = a.passEst;
  if (!pe || !(a.kind || "").toLowerCase().includes("combine")) return null;
  const fmtP = (x) => (typeof x === "number" ? (x >= 10 ? x.toFixed(0) : x.toFixed(1)) + "%" : "—");
  const fmtT = (x) => (typeof x === "number" ? Math.round(x) + "d" : "—");
  const ageMin = a.passEstLastUs ? Math.floor((Date.now() * 1000 - a.passEstLastUs) / 60_000_000) : null;
  const stale = ageMin != null && ageMin > 180;
  if (pe.locked || !pe.raw) {
    return (
      <div className="m-acct-mll-row mono" style={{ marginTop: 4, opacity: stale ? 0.5 : 1 }}>
        <span className="muted">P(pass)</span>
        <span className="tnum neg">{pe.locked ? "0% · LOCKED" : "n/a · " + (pe.note || "unmodeled")}</span>
      </div>
    );
  }
  const r = pe.raw;
  const p30 = r.horizons && r.horizons["30d"];
  return (
    <div className="m-acct-mll-row mono" style={{ marginTop: 4, opacity: stale ? 0.5 : 1 }}>
      <span>
        <span className="muted">P(pass) </span>
        <span className={"tnum " + ((p30 || 0) >= 30 ? "pos" : "")}>{fmtP(p30)}</span>
        <span className="muted"> 30d · </span>
        <span className="tnum">{fmtP(r.p_pass_eventual)}</span>
        <span className="muted"> evt{stale ? " · stale" : ""}</span>
      </span>
      <span>
        <span className="muted">T </span>
        <span className="tnum">{typeof r.t_pass_p50 === "number" ? "~" + fmtT(r.t_pass_p50) : "—"}</span>
        <span className="muted"> · ≤</span>
        <span className="tnum">{fmtT(r.t_pass_p90)}</span>
      </span>
    </div>
  );
}

/* One live strategy row on an account card.  LED semantics mirror desktop:
 * pulse only while the strategy owns an open position; running-but-flat is
 * a steady dot; grace/cooldown surfaces a [BLOCK] chip. */
function MInstRow({ a, s }) {
  const agentStatus = (a.strategyStatus && a.strategyStatus[s.defId]) || null;
  const lastSig = agentStatus || s.lastSig || "";
  const hasErr = /\b(error|err|fault|failed)\b/i.test(lastSig);
  const graceMatch = lastSig.match(/\b(?:grace|blocked|cooldown|lockout|entry[_\-]?block)\s*([0-9]+\s*[smhd])?(?:\/[0-9]+\s*[smhd])?/i);
  const isGrace = !!graceMatch;
  const ledState =
      s.state === "stopped"   ? "stopped"
    : s.state === "breached"  ? "breached"
    : hasErr                  ? "error"
    : s.hasOpenPos            ? "running"
    : isGrace                 ? "blocked"
    : s.state === "paused"    ? "paused"
    : s.state === "armed"     ? "armed"
    :                           "waiting";
  // Strip the agent-embedded "P&L=±$N" token (same number renders as
  // m-inst-pnl on the right), compact "side entry=N" → "side@N", inject
  // the live contract count + symbol so e.g. mr reads "short@30000 1c MNQ".
  const liveCt = Number(s.openContracts) || 0;
  const posAgeS = s.posLastUs
    ? Math.floor((Date.now() * 1000 - s.posLastUs) / 1_000_000)
    : null;
  const posStale = posAgeS != null && posAgeS > 300;
  const posSym = (s.posSymbol || "").trim();
  const compactPrices = (str) =>
    str.replace(/-?\d+\.\d+/g, (m) => {
      const n = Number(m);
      return Math.abs(n) >= 100 ? String(Math.round(n)) : m;
    });
  let sigTxt = compactPrices(
    lastSig
      .replace(/\s*\bP&L=[+\-−]?\$?-?\d[\d.,]*/g, "")
      .replace(/\b(short|long)\s+entry=/gi, "$1@")
      .replace(/\b(short|long)\s+x\d+(?=@)/gi, "$1")
  ).trim();
  if (liveCt > 0 && !posStale) {
    const ctTxt = posSym ? `${liveCt}c ${posSym}` : `${liveCt}c`;
    const m = sigTxt.match(/^(short|long)@\S+/i);
    if (m) sigTxt = m[0] + ` ${ctTxt}` + sigTxt.slice(m[0].length);
    else sigTxt = `${ctTxt} · ${sigTxt}`;
  } else if (liveCt > 0 && posStale) {
    sigTxt = sigTxt + ` · ${posAgeS > 3600 ? Math.round(posAgeS / 3600) + "h" : Math.round(posAgeS / 60) + "m"} old`;
  }
  // Unrealized straight from the account's live positions (STRATS.unrealized
  // can lag a.positions, freezing the per-strategy P&L).
  const unreal = (() => {
    const live = (a.positions || []).filter((p) => p && p.strategy_id === s.defId);
    return live.length
      ? live.reduce((t, p) => t + (Number(p.unrealized) || 0), 0)
      : (Number(s.unrealized) || 0);
  })();
  const livePnl = (Number(s.today) || 0) + unreal;
  return (
    <div className="m-inst-row" data-state={ledState}>
      <span className="m-inst-led" />
      <div className="m-inst-body">
        <div className="m-inst-name mono">{s.name}</div>
        <div className="m-inst-meta mono">
          {s.symbol && <><span>{s.symbol}</span><span className="m-sep">·</span></>}
          {s.contracts ? <><span>{s.contracts}c</span><span className="m-sep">·</span></> : null}
          {isGrace && (
            <>
              <span
                title={`agent reports entry-block (${graceMatch[0]}). Strategy will not enter even on a valid signal until the block clears.`}
                style={{
                  fontSize: 9.5, letterSpacing: "0.06em", padding: "0 4px",
                  border: "1px solid var(--warn)", color: "var(--warn)",
                }}>
                [BLOCK]{graceMatch[1] ? " " + graceMatch[1] : ""}
              </span>
              <span className="m-sep">·</span>
            </>
          )}
          {sigTxt
            ? <span className="m-inst-sig">{sigTxt}</span>
            : s.lastSig
              ? <span className="muted">{window.cleanSig(s.lastSig)}</span>
              : s.state !== "stopped"
                ? <span className="muted" style={{ fontStyle: "italic", opacity: 0.6 }}
                    title="agent hasn't emitted strategyStatus for this variant yet">
                    no status emit
                  </span>
                : <span className="muted">—</span>}
        </div>
      </div>
      <div className={"m-inst-pnl mono tnum " + (livePnl > 0 ? "pos" : livePnl < 0 ? "neg" : "muted")}
           title={s.hasOpenPos && unreal !== 0
             ? `realized today ${window.fmt$(s.today || 0, 2)} + open ${window.fmt$(unreal, 2)}`
             : undefined}>
        {livePnl === 0 ? "—" : window.fmt$(livePnl, 0)}
      </div>
    </div>
  );
}

function MAccountCard({ a, defaultOpen = false }) {
  const [open, setOpen] = mState(defaultOpen);
  const [busy, setBusy] = mState(null);
  const [err, setErr] = mState(null);
  const { loading: mLoading } = useMLoaded();
  const stratsLoading = mLoading("strats");
  // Status panel shows LIVE strategies only (parity with desktop
  // AccountCard).  Stopped variants stay in STRATS for the
  // dedicated strategy/account history views but render as noisy
  // "—" rows here next to the live ones.
  const allStrats = (window.STRATS || []).filter(
    (s) => s.acct === a.id && (!a.instance || s.instance === a.instance));
  const strats = allStrats.filter(s => s.state !== "stopped");
  const stoppedCount = allStrats.length - strats.length;
  const external = mIsExternalAcct(a);
  const preStaged = window.acctPreStaged && window.acctPreStaged(a);
  const pausedCount = a.strategyPaused ? Object.keys(a.strategyPaused).length : 0;
  // Profit-target resolution — desktop AccountCard parity:
  //   agent goal_state target → static tier rule fallback → consistency-
  //   adjusted effective target (best_day / consistency_pct can push the
  //   real finish line past the static $ target).
  let targetAbs = Number(a.target) || 0;
  if (!targetAbs && typeof window.ACCOUNT_KIND_RULES === "function") {
    const r = window.ACCOUNT_KIND_RULES(a.kind, a.initial_capital);
    if (r && r.target_dollars > 0) targetAbs = r.target_dollars;
  }
  const consistency = Number(a.consistencyPct) || 0;
  const bestDay = Math.max(Number(a.bestDayPnl) || 0, Number(a.todayGoalPnl) || 0);
  let effTarget = Number(a.effectiveTarget) || 0;
  if (!effTarget && targetAbs > 0 && consistency > 0 && bestDay > 0) {
    effTarget = Math.max(targetAbs, bestDay / consistency);
  }
  if (!effTarget) effTarget = targetAbs;
  const targetBumped = effTarget > targetAbs + 0.5;
  const sessStart = Number(a.sessionStart) || Number(a.equity) || Number(a.initial_capital) || 0;
  const target = effTarget > 0 ? sessStart + effTarget : null;
  // resolvedMllFloor corrects XFA, where the agent reports the combine-style
  // floor (tier−headroom, e.g. $145,500) instead of the XFA −headroom floor
  // (e.g. −$4,500); the raw floor made mobile show headroom −$149,319 while
  // desktop correctly showed $682.  Combine/practice are unaffected.
  const mllShown = window.resolvedMllFloor ? window.resolvedMllFloor(a) : (a.mllFloor || 0);
  const lo = mllShown || sessStart * 0.95;
  const hi = target || sessStart * 1.05 || (a.equity || 0) * 1.05 || 1;
  const pos = lo === hi ? 0 : Math.max(0, Math.min(1, ((a.equity || 0) - lo) / (hi - lo)));
  const startPos = lo === hi ? 0 : Math.max(0, Math.min(1, (sessStart - lo) / (hi - lo)));
  const headroom = (a.equity || 0) - mllShown;
  const dayPos = (a.dayPnl || 0) >= 0;
  const kindLabel = (a.kindLabel || a.kind || "acct").toUpperCase();
  // Until the agent's first equity push lands the row reads $0 / 0% of
  // MLL — those are dishonest defaults that look like a flat-lined
  // account.  Shimmer the value slots and the MLL bar fill instead.
  const eqLoading = !a.equityVerified && !preStaged;
  // Offline/stale liveness — parity with desktop AccountCard.  When this
  // account's agent instance is disconnected from the hub, the figures
  // below are the last values it pushed, not live; flag it instead of
  // letting a stale balance read as current.
  const _inst = (window.INSTANCES || []).find(i => i.id === a.instance);
  const _ageUs = Number(a.strategiesLastUs) ? (Date.now() * 1000 - Number(a.strategiesLastUs)) : null;
  const ageMin = _ageUs != null ? Math.floor(_ageUs / 60_000_000) : null;
  const ageTxt = ageMin == null ? null : (ageMin >= 60 ? Math.round(ageMin / 60) + "h" : ageMin + "m");
  // Liveness = the agent heartbeat (inst.lastSeenMs, bumped by every frame
  // incl. the 60s equity heartbeat), not the strategy-status cadence — so a
  // live agent doesn't flash "NO UPDATE" on load while strategiesLastUs still
  // holds the bootstrap's last-persisted (minutes-old) status row. Warn only
  // when the heartbeat is silent AND strategy data is stale. See pages.jsx.
  const _seenAgeMs = _inst && _inst.lastSeenMs ? (Date.now() - _inst.lastSeenMs) : null;
  const agentAlive = _seenAgeMs != null && _seenAgeMs < 300_000;   // heartbeat < 5m
  const isStale = !agentAlive && ageMin != null && ageMin >= 5;
  const instOffline = isStale && (_inst ? _inst.state !== "running" : true);
  async function doFlatten() {
    if (!window.flattenAccount) return;
    // Mobile parity with desktop's confirm gate — Flatten is the single most
    // dangerous control an operator can hit on a phone (sends market-close
    // orders for every open position), so never fire it on a stray tap.
    if (!window.confirm(`Flatten ALL open positions on ${a.label || a.id}?\n\nSends market-close orders for every open position on this account.`)) return;
    setBusy("flatten"); setErr(null);
    try { await window.flattenAccount(a.instance, a.id); }
    catch (e) { setErr(String(e.message || e)); }
    finally { setBusy(null); }
  }

  return (
    <article className={"m-acct" + (open ? " open" : "")}>
      {preStaged && (
        <MAcctBanner kind="warn"
          title="Added to the hub but not yet activated on the agent — add a strategy to activate.">
          ◷ PRE-STAGED · add a strategy to activate
        </MAcctBanner>
      )}
      {a.handsOff && (
        <MAcctBanner kind="accent"
          title="HANDS-OFF: manual control — the system will NOT flatten positions you open and the protective drawdown lock is suppressed. TopStep's real MLL still applies.">
          ✋ HANDS-OFF · manual control
        </MAcctBanner>
      )}
      {a.dllDayLock && (
        <MAcctBanner kind="warn"
          title="Broker Daily Loss Limit hit — session locked for today only. Not an account failure; entries resume next trading day.">
          ◉ DLL LOCK · resumes next session
        </MAcctBanner>
      )}
      {pausedCount > 0 && !a.handsOff && (
        <MAcctBanner kind="warn"
          title="Trading paused — new entries halted for the paused strategies; open positions left as-is.">
          ⏸ PAUSED · entries halted{pausedCount > 1 ? ` (${pausedCount})` : ""}
        </MAcctBanner>
      )}
      {isStale && (
        <MAcctBanner kind={instOffline ? "neg" : "warn"}
          title={instOffline
            ? "Agent disconnected — values are the last it pushed, not live."
            : "Agent connected but silent — values may be stale."}>
          {instOffline
            ? `AGENT OFFLINE${ageTxt ? ` · as of ${ageTxt} ago` : " · not live"}`
            : `NO UPDATE ${ageTxt} · agent quiet`}
        </MAcctBanner>
      )}
      <button className="m-acct-head" onClick={() => setOpen(o => !o)}>
        <div className="m-acct-id">
          <span className={"m-acct-badge " + (a.kind || "unknown")}>{kindLabel}</span>
          <div className="m-acct-name-block">
            <div className="m-acct-name">{a.label}</div>
            <div className="m-acct-sub mono">
              {a.broker || "—"} · {a.code || a.label}
              {external && <span className="warn" title="externally-managed account mirrored read-only — aviaf places no orders here"> · mirror</span>}
            </div>
          </div>
        </div>
        <div className="m-acct-eq" style={isStale ? { opacity: 0.5 } : undefined}>
          {preStaged ? (
            <>
              <div className="m-acct-eq-num mono tnum muted">—</div>
              <div className="m-acct-eq-day mono tnum muted">no data</div>
            </>
          ) : (
            <>
              <div className="m-acct-eq-num mono tnum">
                {eqLoading ? <MSkel w={84} h={15} /> : window.fmt$(a.equity || 0, 0)}
              </div>
              <div className={"m-acct-eq-day mono tnum " + (dayPos ? "pos" : "neg")}>
                {eqLoading
                  ? <MSkel w={56} h={11} />
                  : <>{(a.dayPnl || 0) >= 0 ? "+" : ""}{window.fmt$(a.dayPnl || 0, 0)}</>}
              </div>
            </>
          )}
        </div>
        <span className="m-acct-chev" aria-hidden>▾</span>
      </button>
      {!preStaged && (
      <div className="m-acct-mll" style={isStale ? { opacity: 0.5 } : undefined}>
        <div className="m-acct-mll-row mono">
          <span>MLL <span className="tnum">{
            eqLoading ? <MSkel w={56} h={10} /> : window.fmt$(mllShown, 0)
          }</span></span>
          <span title={targetBumped
            ? `static target ${window.fmt$(targetAbs, 0)} pushed to ${window.fmt$(effTarget, 0)} by the consistency rule (best day ${window.fmt$(bestDay, 0)})`
            : undefined}>
            {target
              ? <>target <span className="tnum">{window.fmt$(target, 0)}</span>{targetBumped ? <span className="warn">↑</span> : null}</>
              : a.kind === "xfa" ? <span className="pos">payout</span> : "survival"}
          </span>
        </div>
        <div className={"m-acct-mll-bar" + (eqLoading ? " skel" : "")}>
          <div className="m-acct-mll-fill" style={{ width: (pos * 100).toFixed(2) + "%" }} />
          <div className="m-acct-mll-tick" style={{ left: (startPos * 100).toFixed(2) + "%", background: "var(--muted)" }} />
          {target && <div className="m-acct-mll-tick" style={{ left: "100%", background: "var(--accent)" }} />}
        </div>
        <div className="m-acct-mll-row mono muted">
          <span>headroom <span className="tnum">{
            eqLoading ? <MSkel w={56} h={10} /> : window.fmt$(headroom, 0)
          }</span></span>
          <span className="tnum">{eqLoading ? <MSkel w={28} h={10} /> : (pos * 100).toFixed(0) + "%"}</span>
        </div>
        <MPassEst a={a} />
      </div>
      )}
      <div className="m-acct-instances">
        <div className="m-acct-inst-h mono">
          {stratsLoading && allStrats.length === 0
            ? <MSkel w={92} h={10} />
            : strats.length > 0
              ? <>{strats.length} LIVE{stoppedCount > 0 ? ` · ${stoppedCount} STOPPED` : ""}</>
              : stoppedCount > 0
                ? <>{stoppedCount} {stoppedCount === 1 ? "STRATEGY" : "STRATEGIES"} · ALL STOPPED</>
                : <>STRATEGIES</>}
        </div>
        {/* Row source, in priority order:
            1. live rows (running/armed/paused/breached) — the headline;
            2. all-stopped: show the real stopped rows (capped) rather than
               the old infinite roster shimmer — with an offline agent the
               shimmer never resolved and read as "loading forever";
            3. no rows at all yet: named roster shimmer while strats load
               (desktop parity), plain note otherwise. */}
        {strats.length > 0
          ? strats.map((s) => <MInstRow a={a} s={s} key={s.id} />)
          : allStrats.length > 0
            ? allStrats.slice(0, 4).map((s) => <MInstRow a={a} s={s} key={s.id} />)
            : !preStaged && Array.isArray(a.strategies) && a.strategies.length > 0
              ? a.strategies.map((name) => (
                  <div className="m-inst-row" data-state="stopped" key={"roster-" + name} style={{ opacity: 0.8 }}>
                    <span className="m-inst-led" />
                    <div className="m-inst-body">
                      <div className="m-inst-name mono">
                        {typeof window.prettyStrategy === "function" ? window.prettyStrategy(name) : name}
                      </div>
                      <div className="m-inst-meta mono">
                        {stratsLoading ? <MSkel w={110} h={10} /> : <span className="muted">awaiting agent status</span>}
                      </div>
                    </div>
                    {stratsLoading ? <MSkel w={40} h={12} /> : <span className="m-inst-pnl mono muted">—</span>}
                  </div>
                ))
              : stratsLoading
                ? Array.from({ length: 2 }).map((_, i) => (
                    <div className="m-inst-row" key={"sk" + i} aria-busy="true">
                      <span className="m-inst-led" />
                      <div className="m-inst-body">
                        <MSkel w={"60%"} h={12} />
                        <div style={{ marginTop: 4 }}><MSkel w={"40%"} h={10} /></div>
                      </div>
                      <MSkel w={56} h={12} />
                    </div>
                  ))
                : <div className="m-inst-row" data-state="stopped">
                    <span className="m-inst-led" />
                    <div className="m-inst-body">
                      <div className="m-inst-meta mono muted">no strategies attached</div>
                    </div>
                  </div>}
        {strats.length === 0 && allStrats.length > 4 && (
          <div className="mono muted" style={{ fontSize: 10, padding: "4px 0" }}>
            +{allStrats.length - 4} more stopped · open account for history
          </div>
        )}
        <div hidden={!open}>
          {stoppedCount > 0 && (
            <a className="m-inst-row" href={`#/accounts/${encodeURIComponent(a.id)}`}
               style={{ textDecoration: "none", color: "var(--muted)", fontSize: 11, padding: "6px 0", borderTop: "1px dashed var(--rule)" }}
               title="Open account detail to inspect stopped/historical variants">
              +{stoppedCount} stopped · history →
            </a>
          )}
          {err && <div className="m-err mono">err: {err}</div>}
          <div className="m-acct-actions">
            <a className="m-btn" href={`#/accounts/${encodeURIComponent(a.id)}`}>Open</a>
            <a className="m-btn" href={`#/instances/${encodeURIComponent(a.instance || "")}`}>Logs</a>
            <button className="m-btn m-btn-danger" onClick={doFlatten} disabled={busy === "flatten"}>
              {busy === "flatten" ? "…" : "Flatten"}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

/* Placeholder card rendered while /api/accounts hasn't resolved.
 * Mirrors MAccountCard's collapsed footprint so the list doesn't
 * jump when the real rows replace these. */
function MAccountCardSkel() {
  return (
    <article className="m-acct" aria-busy="true">
      <div className="m-acct-head" style={{ pointerEvents: "none" }}>
        <div className="m-acct-id">
          <MSkel w={42} h={16} style={{ marginRight: 10 }} />
          <div className="m-acct-name-block">
            <MSkel w={120} h={13} />
            <div style={{ marginTop: 6 }}><MSkel w={92} h={10} /></div>
          </div>
        </div>
        <div className="m-acct-eq">
          <MSkel w={84} h={15} />
          <div style={{ marginTop: 4 }}><MSkel w={56} h={11} /></div>
        </div>
      </div>
      <div className="m-acct-mll">
        <div className="m-acct-mll-row mono"><MSkel w={70} h={10} /><MSkel w={58} h={10} /></div>
        <div className="m-acct-mll-bar skel" />
        <div className="m-acct-mll-row mono muted"><MSkel w={92} h={10} /><MSkel w={28} h={10} /></div>
      </div>
    </article>
  );
}

/* ───── Equity plot (mobile) ──────────────────────────────── */
function MEquityChart() {
  const { loading } = useMLoaded();
  const lSeries = loading("equitySeries");
  // EQUITY_TOTAL is an HOURLY aggregate — the old "portfolio · 60d" label on
  // a slice(-60) was really ~60 hours.  Window the last ~7 days and label
  // with the actual span covered by the points rendered.
  const data = (window.EQUITY_TOTAL || []).slice(-168);
  const spanLbl = data.length >= 48 ? `${Math.round(data.length / 24)}d` : `${data.length}h`;
  if (lSeries || data.length < 2) {
    return (
      <div className="m-chart">
        <div className="m-chart-legend mono">
          <span><span className="m-leg-sw" style={{ background: "var(--accent)" }} /> portfolio</span>
          <MSkel w={56} h={11} />
        </div>
        <div className="skel" style={{ height: 130, width: "100%" }} />
      </div>
    );
  }
  const w = 360, h = 130;
  const lo = Math.min(...data), hi = Math.max(...data);
  const span = hi - lo || 1;
  const pts = data.map((v, i) => [
    8 + (i / (data.length - 1)) * (w - 16),
    h - 14 - ((v - lo) / span) * (h - 24),
  ]);
  const d = pts.map((p, i) => (i === 0 ? "M" : "L") + p[0].toFixed(1) + "," + p[1].toFixed(1)).join("");
  const fill = d + ` L${w - 8},${h - 8} L8,${h - 8} Z`;
  const ticks = [0, 0.5, 1];
  const lastV = data.at(-1);
  const firstV = data[0];
  const delta = lastV - firstV;
  return (
    <div className="m-chart">
      <div className="m-chart-legend mono">
        <span><span className="m-leg-sw" style={{ background: "var(--accent)" }} /> portfolio · {spanLbl}</span>
        <span className={delta >= 0 ? "pos" : "neg"}>{window.fmt$(delta, 0)}</span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="m-chart-svg">
        {ticks.map((t, i) => (
          <line key={i} x1="8" x2={w - 8} y1={14 + t * (h - 24)} y2={14 + t * (h - 24)} stroke="var(--rule)" strokeDasharray="1 3" />
        ))}
        <path d={fill} fill="var(--accent)" opacity="0.10" />
        <path d={d} fill="none" stroke="var(--accent)" strokeWidth="1.4" />
        <circle cx={pts.at(-1)[0]} cy={pts.at(-1)[1]} r="3" fill="var(--accent)" />
      </svg>
    </div>
  );
}

/* ───── Strategies live list ──────────────────────────────── */
function MStratList({ limit, all }) {
  const { loading } = useMLoaded();
  const lStrats = loading("strats");
  // Ours-only, live deployments first.  `all` additionally lists stopped +
  // retired rows (strategies tab); the overview slice stays live-only.
  const stateRank = { running: 0, armed: 1, paused: 2, breached: 3, stopped: 5 };
  const rows = mOwnStrats()
    .filter((s) => all || (s.state !== "stopped" && !s.retired))
    .sort((a, b) =>
      (stateRank[a.state] ?? 4) - (stateRank[b.state] ?? 4)
      || window.safeN(b.pnl30, 0) - window.safeN(a.pnl30, 0));
  if (lStrats) {
    const n = limit || 4;
    return (
      <ul className="m-strat-list">
        {Array.from({ length: n }).map((_, i) => (
          <li key={"sk" + i} className="m-strat-row" aria-busy="true">
            <span className="m-strat-led" />
            <div className="m-strat-body">
              <div className="m-strat-name mono"><MSkel w={"60%"} h={13} /></div>
              <div className="m-strat-meta mono" style={{ marginTop: 4 }}>
                <MSkel w={"40%"} h={10} />
              </div>
            </div>
            <div className="m-strat-pnl">
              <MSkel w={56} h={13} />
              <div style={{ marginTop: 3 }}><MSkel w={68} h={10} /></div>
            </div>
          </li>
        ))}
      </ul>
    );
  }
  const shown = limit ? rows.slice(0, limit) : rows;
  if (shown.length === 0) {
    const stopped = mOwnStrats().filter((s) => s.state === "stopped" || s.retired).length;
    return (
      <div className="m-empty mono muted">
        no live strategies{stopped > 0 ? ` · ${stopped} stopped` : ""} — deploy from an account page
      </div>
    );
  }
  return (
    <ul className="m-strat-list">
      {shown.map(r => {
        const today = window.safeN(r.today, 0);
        return (
          <li key={r.id} className="m-strat-row" data-state={r.state}>
            <span className="m-strat-led" />
            <div className="m-strat-body">
              <div className="m-strat-name mono">
                {r.name} <span className="muted">· {r.symbol || "—"}</span>
              </div>
              <div className="m-strat-meta mono">
                <span>{r.acct}{r.retired ? <span className="muted"> · retired</span> : null}</span>
                <span className="m-sep">·</span>
                <span>WR <b>{Number.isFinite(r.wr) ? (r.wr * 100).toFixed(1) + "%" : "—"}</b></span>
                <span className="m-sep">·</span>
                <span>PF <b>{!Number.isFinite(r.pf) ? (r.pf === Infinity ? "∞" : "—") : r.pf.toFixed(2)}</b></span>
              </div>
            </div>
            <div className="m-strat-pnl">
              <div className={"mono tnum " + (today > 0 ? "pos" : today < 0 ? "neg" : "muted")}>
                {today === 0 ? "—" : window.fmt$(today, 0)}
              </div>
              <div className="mono tnum muted m-strat-30">
                {window.fmt$(window.safeN(r.pnl30, 0), 0)} <span className="m-sep">·</span> 30d
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/* ───── Feed (activity) ───────────────────────────────────── */
function MFeed({ limit, tag }) {
  const { loading } = useMLoaded();
  if (loading("feed")) {
    const n = limit || 5;
    return (
      <ul className="m-feed">
        {Array.from({ length: n }).map((_, i) => (
          <li key={"sk" + i} className="m-feed-row" aria-busy="true">
            <span className="m-feed-tag mono"><MSkel w={36} h={10} /></span>
            <div className="m-feed-body">
              <div className="m-feed-txt mono"><MSkel w={"82%"} h={12} /></div>
              <div className="m-feed-t mono muted" style={{ marginTop: 3 }}>
                <MSkel w={64} h={9} />
              </div>
            </div>
          </li>
        ))}
      </ul>
    );
  }
  const rows = (window.FEED || []).filter((f) => !tag || tag === "all" || f.tag === tag);
  const data = limit ? rows.slice(0, limit) : rows;
  if (data.length === 0) {
    return <div className="m-empty mono muted">no events{tag && tag !== "all" ? ` · ${tag}` : ""}</div>;
  }
  return (
    <ul className="m-feed">
      {data.map((f, i) => (
        <li key={i} className="m-feed-row">
          <span className={"m-feed-tag mono " + f.tag}>{f.tag.toUpperCase()}</span>
          <div className="m-feed-body">
            <div className="m-feed-txt mono">{f.txt}</div>
            <div className="m-feed-t mono muted">{f.t}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}

/* ───── Health row (compact) ──────────────────────────────── */
function MHealth() {
  // Same shared derivation desktop's HealthPanel uses (defined in
  // data-live.jsx).  Previously this was 5 hardcoded mock rows that
  // read identically on a green hub and on a dead hub — useless.
  // Now every value is derived from live window state.
  //
  // 1-second tick keeps "Ns ago" rows decreasing between WS frames.
  // Also re-evaluates staleness windows so a collector that goes
  // silent flips to "stale" within 30 s even if no other WS activity
  // would otherwise re-render the panel.
  const [, _tickH] = mState(0);
  mEffect(() => {
    const h = setInterval(() => _tickH(t => (t + 1) | 0), 1000);
    return () => clearInterval(h);
  }, []);
  const items = (window.computeHealthItems && window.computeHealthItems()) || [];
  return (
    <ul className="m-health">
      {items.map((it, i) => (
        <li key={i} className="m-health-row">
          <span className={"m-health-dot " + it.v} />
          <span className="m-health-k mono">{it.k}</span>
          <span className="m-health-x mono muted">{it.x}</span>
        </li>
      ))}
    </ul>
  );
}

/* ───── Critical alert callout (overview) ─────────────────── */
function MAlertCallout({ goto }) {
  const { loading } = useMLoaded();
  if (loading("alerts")) {
    return (
      <div className="m-alert-compact mono" aria-busy="true">
        <span className="t"><MSkel w={42} h={10} /></span>
        <MSkel w={56} h={10} />
        <MSkel w={56} h={10} />
        <MSkel w={56} h={10} />
        <span className="m-alc-arrow muted">›</span>
      </div>
    );
  }
  // window.ALERTS holds RAW DAL rows — normalize before reading sev/ack/head
  // (reading a.sev off raw rows made every count 0 and the expanded callout
  // render an empty head/msg).
  const raw = window.ALERTS || [];
  const all = window.normalizeAlertRow ? raw.map(window.normalizeAlertRow) : raw;
  const sevRank = { crit: 3, warn: 2, info: 1 };
  const counts = {
    crit: all.filter((a) => mAlSev(a) === "crit" && !mAlAck(a)).length,
    warn: all.filter((a) => mAlSev(a) === "warn" && !mAlAck(a)).length,
    info: all.filter((a) => mAlSev(a) === "info" && !mAlAck(a)).length,
  };
  const unacked = all.filter((a) => !mAlAck(a)).sort((a, b) => sevRank[mAlSev(b)] - sevRank[mAlSev(a)]);
  const top = unacked[0];
  const open = () => goto ? goto("alerts") : (location.hash = "#/alerts");

  // minimal mode: no crit/warn → just a counts banner
  if (!top || (top.sev !== "crit" && top.sev !== "warn")) {
    return (
      <button className="m-alert-compact mono" onClick={open}>
        <span className="t">ALERTS</span>
        <span className="m-alc-cnt crit"><span className="d" /> {counts.crit} crit</span>
        <span className="m-alc-cnt warn"><span className="d" /> {counts.warn} warn</span>
        <span className="m-alc-cnt info"><span className="d" /> {counts.info} info</span>
        <span className="m-alc-arrow">›</span>
      </button>);
  }

  return (
    <button className="m-alert-full" data-sev={top.sev} onClick={open}>
      <div className="m-alc-h">
        <span className={"m-alc-sev " + top.sev + " mono"}>{top.sev.toUpperCase()}</span>
        <span className="m-alc-meta mono muted">{top.source}{top.account ? " · " + top.account : ""}</span>
        <span className="m-alc-age mono muted">{top.age}</span>
      </div>
      <div className="m-alc-head" title={top.msg}>{top.head}</div>
      <div className="m-alc-msg mono">{top.msg}</div>
      <div className="m-alc-foot mono">
        <span>
          {counts.crit > 0 && <span className="m-alc-cnt crit"><span className="d" /> {counts.crit} crit</span>}
          {counts.warn > 0 && <span className="m-alc-cnt warn" style={{marginLeft: counts.crit ? 10 : 0}}><span className="d" /> {counts.warn} warn</span>}
        </span>
        <span className="m-alc-link">View all alerts ›</span>
      </div>
    </button>);
}

/* ───── Overview screen ───────────────────────────────────── */
/* Accounts ordered by operational relevance: accounts with running
 * strategies first (that's what the operator opens the phone to check),
 * then by equity.  Externally-mirrored accounts sink to the bottom. */
function mSortAccts(accts) {
  const liveN = (a) => (window.STRATS || []).filter(
    (s) => s.acct === a.id && (s.state === "running" || s.state === "armed" || s.state === "paused")).length;
  return [...accts].sort((x, y) => {
    const ex = mIsExternalAcct(x) ? 1 : 0, ey = mIsExternalAcct(y) ? 1 : 0;
    if (ex !== ey) return ex - ey;
    const lx = liveN(x), ly = liveN(y);
    if (lx !== ly) return ly - lx;
    return (y.equity || 0) - (x.equity || 0);
  });
}

function MOverview({ goto }) {
  const { loading } = useMLoaded();
  const acctsLoading = loading("accounts");
  const stratsLoading = loading("strats");
  const accts = mSortAccts(window.ACCOUNTS || []);
  const ownDeployed = mOwnStrats().filter((s) => s.state !== "stopped" && !s.retired);
  return (
    <>
      <MStatusStrip />
      <MHero />
      <MKpis />

      <MAlertCallout goto={goto} />

      <MSection title="ACCOUNTS"
        count={acctsLoading ? undefined : accts.length}
        action="see all ▸" onAction={() => goto("accounts")}>
        <div className="m-acct-list">
          {acctsLoading && accts.length === 0
            ? Array.from({ length: 2 }).map((_, i) => <MAccountCardSkel key={"sk" + i} />)
            : accts.map((a) => <MAccountCard a={a} key={a.id} />)}
        </div>
      </MSection>

      <MSection title="PERFORMANCE">
        <MEquityChart />
      </MSection>

      <MSection title="STRATEGIES · LIVE"
        count={stratsLoading ? undefined : ownDeployed.length}
        action="all ▸" onAction={() => goto("strategies")}>
        <MStratList limit={5} />
      </MSection>

      <MSection title="ACTIVITY" action="feed ▸" onAction={() => goto("activity")}>
        <MFeed limit={5} />
      </MSection>

      <MSection title="SYSTEM">
        <MHealth />
      </MSection>

      <div className="m-bottom-pad" />
    </>
  );
}

/* ───── Accounts screen ───────────────────────────────────── */
function MAccounts() {
  const { loading } = useMLoaded();
  const [filter, setFilter] = mState("all");
  const accts = mSortAccts(window.ACCOUNTS || []);
  const acctsLoading = loading("accounts");
  // Filter chips derive from the kinds actually present (the old hardcoded
  // ALL/LIVE/PAPER/XFA listed kinds this fleet doesn't run and omitted the
  // ones it does, so most chips filtered to an empty list).
  const kinds = [...new Set(accts.map((a) => a.kind || "other"))];
  const chips = [["all", `ALL · ${accts.length}`]].concat(
    kinds.map((k) => [k, `${k.toUpperCase()} · ${accts.filter((a) => (a.kind || "other") === k).length}`]));
  const filt = accts.filter(a => filter === "all" || (a.kind || "other") === filter);
  return (
    <>
      {chips.length > 2 && (
        <div className="m-segctl mono">
          {chips.map(([k, l]) => (
            <button key={k} data-active={filter === k} onClick={() => setFilter(k)}>{l}</button>
          ))}
        </div>
      )}
      <div className="m-acct-list dense">
        {acctsLoading && accts.length === 0
          ? Array.from({ length: 3 }).map((_, i) => <MAccountCardSkel key={"sk" + i} />)
          : filt.map((a) => <MAccountCard a={a} key={a.id} />)}
      </div>
      <div className="m-bottom-pad" />
    </>
  );
}

/* ───── Strategies risk × return map (mobile) ─────────────── */
function MStrategyMap() {
  // Live-rev subscription: useMLoaded() bumps a tick every __LIVE.rev
  // change, so fills/equity pushes refresh the map without the operator
  // having to navigate away.  Same pattern desktop's StrategyRiskMap uses.
  useMLoaded();
  // mobile mirrors desktop: show every deployed strategy, fade stopped.
  // Bare-class slugs ("mr"/"orb"/…) and `_legacy` suffixed defIds are
  // visual noise on the map (they read as duplicates of the versioned
  // bubble the operator thinks of as the real deployment); hide them by
  // default to match desktop's StrategyRiskMap.
  const rawAll = window.getAllDeployedStrats ? window.getAllDeployedStrats() : (window.STRATS || []);
  const classIdSet = new Set((window.STRATEGY_CLASSES || []).map((c) => c.id));
  const isBareOrLegacy = (s) =>
    classIdSet.has(s.defId) || (s.defId && s.defId.endsWith("_legacy"));
  const all = rawAll.filter((s) => !isBareOrLegacy(s));
  const live = (window.getLiveStrats() || []).filter((s) => !isBareOrLegacy(s));
  const [selectedId, setSelectedId] = mState(null);
  const [hidden, setHidden] = mState(() => new Set());
  const [xMode, setXMode] = mState("weekly");
  const [showSurv, setShowSurv] = mState(true);
  const [showStopped, setShowStopped] = mState(true);
  const selected = all.find((s) => s.id === selectedId) || null;
  const liveSet = window.LIVE_STATES || new Set(["running", "armed", "paused"]);
  // dynamic class list — surfaces variants whose class isn't in STRATEGY_CLASSES
  const classesInData = new Set(all.map((s) => window.classOfStrat(s)));
  const knownIds = new Set((window.STRATEGY_CLASSES || []).map((c) => c.id));
  const extraClasses = [...classesInData]
    .filter((k) => k && !knownIds.has(k))
    .map((k) => window.classMeta(k));
  const classes = [...(window.STRATEGY_CLASSES || []), ...extraClasses];

  const visible = all.filter((s) => {
    if (hidden.has(window.classOfStrat(s))) return false;
    if (!showStopped && !liveSet.has(s.state)) return false;
    return true;
  });
  // Mode-windowed fields — see pages.jsx StrategyRiskMap for the
  // rationale (pnl30 used to be all-time, Weekly = /4.3 lied).
  const pnlField = xMode === "weekly" ? "pnl7" : "pnl30";
  const ddField  = xMode === "weekly" ? "dd7"  : "dd30";
  const pnlOf = (s) => window.safeN(s[pnlField], 0);
  const xVal = (s) => pnlOf(s);
  const yVal = (s) => Math.abs(window.safeN(s[ddField], 0));

  const w = 360, h = 300, padL = 46, padR = 12, padT = 14, padB = 32;
  // axis bounds derived from ALL strats (live + stopped) so faded
  // bubbles aren't pinned to the edge by tighter live-only bounds.
  const xsRaw = all.map(xVal), ysRaw = all.map(yVal);
  let xMin = Math.min(0, ...(xsRaw.length ? xsRaw : [0])),
      xMax = Math.max(0.01, ...(xsRaw.length ? xsRaw : [1]));
  let yMax = Math.max(1, ...(ysRaw.length ? ysRaw : [1]));
  if (showSurv) yMax = Math.max(yMax, 5000);
  const xSpan = (xMax - xMin) || 1;
  xMin -= xSpan * 0.10; xMax += xSpan * 0.10; yMax *= 1.08;

  const X = (v) => padL + (v - xMin) / (xMax - xMin) * (w - padL - padR);
  const Y = (v) => padT + (1 - v / yMax) * (h - padT - padB);

  const xTicks = window.niceTicks(xMin, xMax, 4);
  const yTicks = window.niceTicks(0, yMax, 4);
  const fmt$ = (v) => {
    const a = Math.abs(v);
    return (v < 0 ? "−" : "") + "$" + (a >= 1000 ? (a / 1000).toFixed(1) + "k" : a.toFixed(0));
  };

  // ── viewBox state ──
  const baseVB = { x: 0, y: 0, w, h };
  const [vb, setVb] = mState(baseVB);
  const vbRef = mRef(vb); vbRef.current = vb;
  const svgRef = mRef(null);
  const touchRef = mRef({ mode: 0, lastDist: 0, lastX: 0, lastY: 0, downX: 0, downY: 0, moved: 0 });

  const ZOOM_MIN = 0.92, ZOOM_MAX = 8;
  const zoomLevel = w / vb.w;

  const zoomAt = (clientX, clientY, factor) => {
    const el = svgRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const u = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    const v = Math.max(0, Math.min(1, (clientY - r.top) / r.height));
    const cur = vbRef.current;
    const minW = w / ZOOM_MAX, maxW = w / ZOOM_MIN;
    const tw = cur.w * factor;
    const nw = Math.max(minW, Math.min(maxW, tw));
    if (nw === cur.w) return;
    const eff = nw / cur.w;
    const nh = cur.h * eff;
    const nx = cur.x + (cur.w - nw) * u;
    const ny = cur.y + (cur.h - nh) * v;
    setVb({ x: nx, y: ny, w: nw, h: nh });
  };
  const panBy = (dxPx, dyPx) => {
    const el = svgRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const dx = (dxPx / r.width) * vbRef.current.w;
    const dy = (dyPx / r.height) * vbRef.current.h;
    setVb((p) => ({ ...p, x: p.x - dx, y: p.y - dy }));
  };
  const reset = () => setVb(baseVB);

  // attach native touch/wheel handlers (passive: false)
  mEffect(() => {
    const el = svgRef.current; if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const dy = Math.max(-40, Math.min(40, e.deltaY));
      zoomAt(e.clientX, e.clientY, Math.exp(dy * 0.0028));
    };
    const onTouchStart = (e) => {
      if (e.touches.length === 1) {
        // Single finger: record start for tap detection only.  Do not
        // pan and do not preventDefault — the page must still be able
        // to scroll vertically through the chart.  Bubble selection
        // still fires through the SVG <g onClick>.
        touchRef.current = {
          mode: 0,
          lastX: e.touches[0].clientX, lastY: e.touches[0].clientY,
          downX: e.touches[0].clientX, downY: e.touches[0].clientY,
          moved: 0, lastDist: 0,
        };
      } else if (e.touches.length === 2) {
        // Two fingers: enter pinch+pan mode and start intercepting.
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        touchRef.current = {
          mode: 2,
          lastDist: Math.sqrt(dx * dx + dy * dy),
          lastX: (e.touches[0].clientX + e.touches[1].clientX) / 2,
          lastY: (e.touches[0].clientY + e.touches[1].clientY) / 2,
          moved: 0,
        };
      }
    };
    const onTouchMove = (e) => {
      const tr = touchRef.current;
      if (e.touches.length === 1) {
        // Single-finger: track movement so handleBubbleTap can suppress
        // accidental selection if the user is scrolling, but never
        // preventDefault — let the browser's pan-y scroll take over.
        const t = e.touches[0];
        const dx = t.clientX - tr.lastX, dy = t.clientY - tr.lastY;
        tr.moved += Math.abs(dx) + Math.abs(dy);
        tr.lastX = t.clientX; tr.lastY = t.clientY;
        return;
      }
      if (e.touches.length === 2) {
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        if (tr.lastDist > 0) {
          let f = tr.lastDist / dist;
          f = Math.max(0.7, Math.min(1.4, f));
          if (f !== 1) zoomAt(cx, cy, f);
        }
        if (tr.lastX) panBy(cx - tr.lastX, cy - tr.lastY);
        tr.lastDist = dist; tr.lastX = cx; tr.lastY = cy; tr.mode = 2;
        tr.moved += 5;
      }
    };
    const onTouchEnd = (e) => {
      if (e.touches.length === 0) touchRef.current = { mode: 0, lastDist: 0, lastX: 0, lastY: 0, moved: 0 };
      else if (e.touches.length === 1) {
        touchRef.current = { mode: 0, lastX: e.touches[0].clientX, lastY: e.touches[0].clientY, downX: e.touches[0].clientX, downY: e.touches[0].clientY, moved: 0, lastDist: 0 };
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: false });
    el.addEventListener("touchcancel", onTouchEnd, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, []);

  const handleBubbleTap = (s) => {
    // touchRef.current.moved tracks recent pan/pinch
    if (touchRef.current.moved > 6) return;
    setSelectedId(s.id === selectedId ? null : s.id);
  };

  const toggle = (k) => {
    const next = new Set(hidden);
    next.has(k) ? next.delete(k) : next.add(k);
    setHidden(next);
  };

  // survival threshold lines per acct type present (use full deploy list)
  const acctTypesPresent = Array.from(new Set(all.map((s) => window.acctTypeOfStrat(s))));
  const survivalLines = showSurv ? window.ACCT_TYPES
    .filter((t) => t.survival && acctTypesPresent.includes(t.id))
    .map((t) => ({ y: t.survival, label: t.label, color: t.color })) : [];

  // bubble radius
  const radius = (s) => Math.max(5, Math.min(14, 4 + Math.sqrt(window.safeN(s.trades, 6)) * 1.1));

  // sort: bigger drawn first
  const ordered = [...visible].sort((a, b) => window.safeN(b.trades, 0) - window.safeN(a.trades, 0));

  return (
    <div className="m-strat-map">
      {/* control strip */}
      <div className="m-map-ctl mono">
        <div className="m-map-modes">
          <button data-active={xMode === "weekly"} onClick={() => setXMode("weekly")}>WK</button>
          <button data-active={xMode === "monthly"} onClick={() => setXMode("monthly")}>30D</button>
        </div>
        <div className="m-map-legend">
          {classes.map((c) => {
            const cnt = all.filter((s) => window.classOfStrat(s) === c.id).length;
            if (cnt === 0) return null;
            const on = !hidden.has(c.id);
            return (
              <span key={c.id} className="m-map-leg" style={{ color: c.color }} data-on={on} onClick={() => toggle(c.id)}>
                <span className="sw" />
                <span style={{ color: on ? "var(--ink)" : "var(--muted)" }}>{c.label.split(" ")[0]}</span>
                <span className="cnt">{cnt}</span>
              </span>);
          })}
        </div>
        <button className="m-map-surv" data-active={showSurv} onClick={() => setShowSurv(!showSurv)}>⚠ MLL</button>
        <button className="m-map-surv" data-active={showStopped} onClick={() => setShowStopped(!showStopped)} title="Show stopped variants">⏸ STOP</button>
      </div>

      {/* canvas */}
      <div className="m-map-canvas">
        <svg ref={svgRef}
          className="m-map-svg"
          viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
          preserveAspectRatio="xMidYMid meet"
          onDoubleClick={reset}>
          {/* y grid */}
          {yTicks.map((v, i) => (
            <g key={"yt" + i}>
              <line x1={padL} x2={w - padR} y1={Y(v)} y2={Y(v)} stroke="var(--rule)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
              <text x={padL - 6} y={Y(v) + 3} textAnchor="end" className="m-map-ax">{fmt$(v)}</text>
            </g>
          ))}
          {/* x grid + 0 line */}
          {xTicks.map((v, i) => (
            <g key={"xt" + i}>
              <line x1={X(v)} x2={X(v)} y1={padT} y2={h - padB} stroke={v === 0 ? "var(--ink-2)" : "var(--rule)"} strokeWidth="1"
                opacity={v === 0 ? 0.5 : 1} vectorEffect="non-scaling-stroke" />
              <text x={X(v)} y={h - padB + 12} textAnchor="middle" className="m-map-ax">{fmt$(v)}</text>
            </g>
          ))}

          {/* survival lines */}
          {survivalLines.map((sl, i) => (
            <g key={"sv" + i}>
              <line x1={padL} x2={w - padR} y1={Y(sl.y)} y2={Y(sl.y)}
                className="m-map-surv-line" vectorEffect="non-scaling-stroke" />
              <text x={padL + 4} y={Y(sl.y) - 3} className="m-map-surv-l">{sl.label} ${(sl.y / 1000).toFixed(1)}k</text>
            </g>
          ))}

          {/* axis labels */}
          <text x={(padL + w - padR) / 2} y={h - 4} textAnchor="middle" className="m-map-ax" style={{ letterSpacing: "0.1em", textTransform: "uppercase" }}>
            {xMode === "weekly" ? "weekly P&L" : "30d P&L"}
          </text>

          {/* bubbles */}
          {ordered.map((s) => {
            const k = window.classOfStrat(s);
            const kd = window.classMeta(k);
            const cx = X(xVal(s));
            const cy = Y(yVal(s));
            const r = radius(s);
            const isSel = s.id === selectedId;
            const isStopped = !liveSet.has(s.state);
            const ring = s.state === "armed" ? "5 3"
              : s.state === "paused" ? "2 3"
              : (isStopped ? "1 3" : "0");
            const groupOp = isStopped ? 0.42 : 1;
            const fillOp = isSel ? 0.4 : (isStopped ? 0.06 : 0.18);
            return (
              <g key={s.id} style={{ cursor: "pointer", opacity: groupOp }} onClick={() => handleBubbleTap(s)}>
                {isSel && <circle cx={cx} cy={cy} r={r + 4} fill="none" stroke={kd.color} strokeWidth="1.2" opacity="0.6" vectorEffect="non-scaling-stroke" />}
                <circle cx={cx} cy={cy} r={r} fill={kd.color}
                  fillOpacity={fillOp}
                  stroke={kd.color} strokeWidth={isSel ? "2.2" : "1.5"}
                  strokeDasharray={ring} vectorEffect="non-scaling-stroke" />
                <circle cx={cx} cy={cy} r={isSel ? "2.4" : "1.8"} fill={kd.color} />
              </g>);
          })}
        </svg>
        <div className="m-map-zoom mono">
          {(() => {
            const center = (f) => {
              const el = svgRef.current; if (!el) return;
              const r = el.getBoundingClientRect();
              zoomAt(r.left + r.width / 2, r.top + r.height / 2, f);
            };
            return (
              <>
                <button onClick={() => center(1.22)} aria-label="zoom out">−</button>
                <span className="lvl tnum">{zoomLevel.toFixed(2)}×</span>
                <button onClick={() => center(0.82)} aria-label="zoom in">＋</button>
                <button onClick={reset} title="Reset">⌖</button>
              </>);
          })()}
        </div>
        <div className="m-map-hint mono">2-finger pinch · pan · tap bubble → details</div>
      </div>

      {/* selected detail sheet */}
      {selected ? (
        <MMapSelected strat={selected} onClose={() => setSelectedId(null)} />
      ) : all.length === 0 ? (
        <div className="m-map-empty mono">
          <div className="t">No deployed strategies</div>
          <div className="s">nothing to plot — deploy a strategy to see it here</div>
        </div>
      ) : (
        <div className="m-map-empty mono">
          <div className="t">Tap a bubble to inspect</div>
          <div className="s">{live.length} live · {all.length - live.length} stopped · {classesInData.size} class{classesInData.size===1?"":"es"} · 2-finger pinch/pan to zoom</div>
        </div>
      )}

      {/* running class rollup — real 30d/7d sums per class.  The old strip
          drew aggregateCurves() sparklines here: SYNTHETIC deterministic
          walkers scaled so only the endpoint was real — a fake chart shape
          presented as live equity.  Numbers only, all real. */}
      <div className="m-map-classes">
        <div className="m-map-classes-h mono">RUNNING CLASSES</div>
        {classes.map((c) => {
          const members = live.filter((s) => window.classOfStrat(s) === c.id);
          if (members.length === 0) return null;
          const p30 = members.reduce((s, m) => s + window.safeN(m.pnl30, 0), 0);
          const p7 = members.reduce((s, m) => s + window.safeN(m.pnl7, 0), 0);
          return (
            <div key={c.id} className="m-map-class-row">
              <span className="m-map-class-name" style={{ color: c.color }}>● <b style={{ color: "var(--ink)" }}>{c.label}</b> <span className="muted">· {members.length}</span></span>
              <span className="mono tnum muted" style={{ fontSize: 10 }}>wk {window.fmt$(p7, 0)}</span>
              <span className={"m-map-class-v mono tnum " + (p30 >= 0 ? "pos" : "neg")}>{window.fmt$(p30, 0)} <span className="muted">30d</span></span>
            </div>);
        })}
      </div>
    </div>);
}

/* compact selection sheet shown under the map */
function MMapSelected({ strat, onClose }) {
  const def = window.STRATEGY_DEFS.find((d) => d.id === strat.defId);
  const klass = def ? def.klass : "other";
  const kmeta = window.classMeta(klass);
  const acct = window.ACCOUNTS.find((a) => a.id === strat.acct);
  const atype = window.acctTypeMeta(acct ? acct.kind : "other");
  // Live positions come from the owning account's agent-emitted rows,
  // tagged with strategy_id (the old OPEN_POSITIONS[inst] lookup keyed on
  // mock-era `p.strat` ids and always came back empty against live data).
  const positions = (acct && Array.isArray(acct.positions) ? acct.positions : [])
    .filter((p) => p && (p.strategy_id === strat.defId || p.strategy_id == null))
    .map((p) => (window.normalizePosition ? window.normalizePosition(p) : p));

  const survival = atype.survival;
  const wrN     = window.safeN(strat.wr, NaN);
  const pfN     = window.safeN(strat.pf, NaN);
  const eRN     = window.safeN(strat.eR, NaN);
  const sharpeN = window.safeN(strat.sharpe, NaN);
  const sortinoN= window.safeN(strat.sortino, NaN);
  const ddN     = window.safeN(strat.dd, 0);
  const pnl30N  = window.safeN(strat.pnl30, 0);
  const pnl7N   = window.safeN(strat.pnl7, 0);
  const todayN  = window.safeN(strat.today, 0);
  const tradesN = window.safeN(strat.trades, 0);
  const contractsN = window.safeN(strat.contracts, 0);
  const riskN   = window.safeN(strat.riskPerTrade, 0);
  const ddPct = survival ? Math.min(1.05, Math.abs(ddN) / survival) : 0;
  const bench = def ? def.bench : null;

  const [tab, setTab] = mState("perf");

  return (
    <div className="m-sel">
      <div className="m-sel-head">
        <span className="m-sel-led" data-state={strat.state} />
        <span className="m-sel-cls" style={{ color: kmeta.color }}>● {kmeta.label}</span>
        <MStateBadge state={strat.state} />
        <span style={{ flex: 1 }} />
        <button className="m-sel-close" onClick={onClose}>×</button>
      </div>
      <div className="m-sel-title mono">
        <div className="m-sel-name">{strat.name}</div>
        <div className="m-sel-sub">
          <span className="m-sel-acct-b" style={{ color: atype.color }}>{atype.label.toUpperCase()}</span>
          <span style={{ color: "var(--ink)" }}>{acct ? acct.label : strat.acct}</span>
          <span className="muted">· {strat.symbol || (def && def.symbol) || "—"}{strat.venue ? "." + strat.venue : ""} · {contractsN}c · risk {window.fmt$(riskN, 0)}</span>
        </div>
      </div>
      <div className="m-sel-hero">
        <div className="c">
          <div className="l">30D P&L</div>
          <div className={"v mono tnum " + (pnl30N >= 0 ? "pos" : "neg")}>{window.fmt$(pnl30N, 0)}</div>
          <div className="s mono">wk {window.fmt$(pnl7N, 0)}</div>
        </div>
        <div className="c">
          <div className="l">TODAY</div>
          <div className={"v mono tnum " + (todayN === 0 ? "muted" : todayN > 0 ? "pos" : "neg")}>
            {todayN === 0 ? "—" : window.fmt$(todayN, 0)}
          </div>
          <div className="s mono">{window.cleanSig(strat.lastSig) || "—"}</div>
        </div>
        <div className="c">
          <div className="l">MAX DD</div>
          <div className="v mono tnum neg">−{window.fmt$(Math.abs(ddN), 0).replace("−", "").replace("$", "$")}</div>
          {survival ? (
            <div className="s mono"><span className={ddPct > 0.8 ? "neg" : ddPct > 0.5 ? "warn" : "muted"}>{(ddPct * 100).toFixed(0)}%</span> <span className="muted">of MLL</span></div>
          ) : <div className="s mono muted">no cap</div>}
        </div>
      </div>
      <div className="m-sel-tabs mono">
        <button data-active={tab === "perf"} onClick={() => setTab("perf")}>PERF</button>
        <button data-active={tab === "params"} onClick={() => setTab("params")}>PARAMS</button>
        <button data-active={tab === "pos"} onClick={() => setTab("pos")}>POS{positions.length > 0 ? "·" + positions.length : ""}</button>
      </div>
      <div className="m-sel-body">
        {tab === "perf" && (
          <div className="m-sel-perf mono">
            <div className="r">
              <span className="k">WIN RATE</span>
              <span className="v tnum">{Number.isFinite(wrN) ? (wrN * 100).toFixed(1) + "%" : "—"}</span>
              {bench && Number.isFinite(wrN) && <span className={"d tnum " + (wrN - bench.wr >= 0 ? "pos" : "neg")}>{(wrN - bench.wr) >= 0 ? "+" : ""}{((wrN - bench.wr) * 100).toFixed(1)}pp</span>}
            </div>
            <div className="r">
              <span className="k">PROFIT FACTOR</span>
              <span className="v tnum">{!Number.isFinite(pfN) ? (pfN === Infinity ? "∞" : "—") : pfN.toFixed(2)}</span>
              {bench && Number.isFinite(pfN) && <span className={"d tnum " + (pfN - bench.pf >= 0 ? "pos" : "neg")}>{(pfN - bench.pf) >= 0 ? "+" : ""}{(pfN - bench.pf).toFixed(2)}</span>}
            </div>
            <div className="r">
              <span className="k">E[R]</span>
              <span className={"v tnum " + (eRN >= 0 ? "pos" : "neg")}>{window.fmtP(eRN, 3)}</span>
              {bench && Number.isFinite(eRN) && <span className={"d tnum " + (eRN - bench.eR >= 0 ? "pos" : "neg")}>{(eRN - bench.eR) >= 0 ? "+" : ""}{(eRN - bench.eR).toFixed(2)}</span>}
            </div>
            <div className="r">
              <span className="k">SHARPE · SORTINO</span>
              <span className="v tnum">{window.safeFix(sharpeN, 2)} · {window.safeFix(sortinoN, 2)}</span>
              <span className="d muted">{tradesN} trades</span>
            </div>
            <div className="r">
              <span className="k">AVG HOLD</span>
              <span className="v tnum">{strat.avgHold || "—"}</span>
              <span className="d muted">since {strat.since || "—"}</span>
            </div>
          </div>
        )}
        {tab === "params" && (
          <div className="m-sel-params">
            {strat.params ? (
              Object.entries(strat.params).map(([group, ps]) => (
                <div key={group}>
                  <div className="m-sel-param-h mono">{group}</div>
                  {Object.entries(ps).map(([k, v]) => (
                    <div key={k} className="m-sel-param-r mono">
                      <span className="muted">{k}</span>
                      <span className="tnum">{typeof v === "boolean" ? (v ? "true" : "false") : String(v)}</span>
                    </div>
                  ))}
                </div>
              ))
            ) : <div className="m-sel-empty mono">No params loaded.</div>}
          </div>
        )}
        {tab === "pos" && (
          positions.length > 0 ? (
            <div className="m-sel-pos-list">
              {positions.map((p, i) => {
                const side = (p.side || (Number(p.qty) < 0 ? "short" : "long")).toString();
                return (
                  <div key={i} className="m-sel-pos mono">
                    <div className="m-sel-pos-h">
                      <span className={"m-sel-pos-side " + (side === "long" ? "pos" : "neg")}>{side ? side.toUpperCase() : "—"}</span>
                      <span className="m-sel-pos-sym">{p.sym || "—"}</span>
                      <span className="muted">{window.safeN(p.qty, 0)}c</span>
                      <span style={{ flex: 1 }} />
                      <span className={"tnum " + (window.safeN(p.unreal, 0) >= 0 ? "pos" : "neg")}>{window.fmt$(window.safeN(p.unreal, 0), 2)}</span>
                    </div>
                    <div className="m-sel-pos-px mono muted">@ {window.safeFix(p.entry, 2)} → {window.safeFix(p.mark, 2)} · since {p.opened || "—"}</div>
                  </div>
                );
              })}
            </div>
          ) : <div className="m-sel-empty mono">No open positions.</div>
        )}
      </div>
    </div>);
}

/* ───── Strategies screen ─────────────────────────────────── */
function MStrategies() {
  useMLoaded();
  const [tab, setTab] = mState("map");
  const own = mOwnStrats();
  const liveSet = window.LIVE_STATES || new Set(["running", "armed", "paused"]);
  const liveCount = own.filter((s) => liveSet.has(s.state)).length;
  const deployedCount = own.filter((s) => s.state !== "stopped" && !s.retired).length;
  const defs = window.STRATEGY_DEFS || [];
  // Research artifacts are the LIVE hub feeds (autopilot candidates + the
  // agent research_catalog scan).  The old tab rendered data.jsx mock
  // PROPOSALS with dead "Deploy/Dismiss" buttons and a duplicate market-
  // regime block — all fabricated; gone.
  const research = [...(window.RESEARCH_CANDIDATES || []), ...(window.RESEARCH || [])];
  return (
    <>
      <div className="m-segctl mono">
        {[["map", "MAP · " + liveCount],
          ["live", "LIVE · " + deployedCount],
          ["catalog", "CATALOG · " + defs.length],
          ["research", "RESEARCH · " + research.length]].map(([k, l]) => (
          <button key={k} data-active={tab === k} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>
      {tab === "map" && <MStrategyMap />}
      {tab === "live" && <MStratList all />}
      {tab === "catalog" && (defs.length === 0
        ? <div className="m-empty mono muted">No strategy definitions reported yet.</div>
        : <ul className="m-strat-list">
            {defs.map((d) => {
              const b = d.bench || {};
              const hasBench = Number.isFinite(b.wr) || Number.isFinite(b.pf) || Number.isFinite(b.eR);
              return (
                <li key={d.id} className="m-strat-row" style={{ paddingTop: 14, paddingBottom: 14 }}>
                  <div className="m-strat-body" style={{ paddingLeft: 4 }}>
                    <div className="m-strat-name mono">
                      {d.name || d.id}
                      {d.symbol ? <span className="muted"> · {d.symbol}{d.venue ? "." + d.venue : ""}</span> : null}
                    </div>
                    {hasBench && (
                      <div className="m-strat-meta mono">
                        {Number.isFinite(b.wr) && <span>WR <b>{(b.wr * 100).toFixed(1)}%</b></span>}
                        {Number.isFinite(b.wr) && Number.isFinite(b.pf) && <span className="m-sep">·</span>}
                        {Number.isFinite(b.pf) && <span>PF <b>{b.pf.toFixed(2)}</b></span>}
                        {Number.isFinite(b.eR) && <><span className="m-sep">·</span><span>E[R] <b className={b.eR >= 0 ? "pos" : "neg"}>{window.fmtP(b.eR, 2)}</b></span></>}
                      </div>
                    )}
                    {d.summary && <div className="m-strat-summary">{d.summary}</div>}
                  </div>
                </li>);
            })}
          </ul>)}
      {tab === "research" && (research.length === 0
        ? <div className="m-empty mono muted">No research artifacts yet.</div>
        : <ul className="m-research">
            {research.map((r, i) => {
              const meta = [r.tag, r.kind, r.author, r.date].filter(Boolean).join(" · ");
              return (
                <li key={r.id || r.candidate_id || i} className="m-research-row">
                  <div className="m-research-meta-row">
                    <span className="mono muted m-research-meta">{meta ? meta.toUpperCase() : "RESEARCH"}</span>
                    {r.status && <span className="m-r-status mono" data-status={r.status}>{String(r.status).toUpperCase()}</span>}
                  </div>
                  <div className="m-research-title">{r.title || r.name || r.id || r.candidate_id}</div>
                  {r.summary && <div className="m-research-sum">{r.summary}</div>}
                  {Array.isArray(r.produces) && r.produces.length > 0 && (
                    <div className="m-research-produces mono muted">produces: {r.produces.join(" · ")}</div>
                  )}
                </li>);
            })}
          </ul>)}
      <div className="m-bottom-pad" />
    </>
  );
}

/* ───── Market screen ─────────────────────────────────────── */
function MMarket() {
  // Real collector telemetry.  The old screen fed window.genEquity(seed) —
  // a deterministic FAKE walker — into per-symbol sparklines and derived a
  // fake %-change from it.  Every value below is agent-emitted: last price,
  // regime tag, tick rate (with the collector's real per-minute rate ring
  // as the sparkline), lag and gap counts.
  useMLoaded();
  const cols = window.COLLECTORS || [];
  const regime = window.REGIME || { asOf: "--", symbols: [] };
  const cellFor = (sym) => (regime.symbols || []).find((s) => s.sym === sym) || null;
  if (cols.length === 0) {
    return (
      <div className="m-empty mono muted" style={{ padding: "28px 18px", textAlign: "center" }}>
        No collectors reporting.
      </div>
    );
  }
  return (
    <>
      <div className="m-mkt-asof mono muted">collector telemetry · as of {regime.asOf}</div>
      <ul className="m-mkt-list">
        {cols.map((c, i) => {
          const sym = (c.primarySymbol && c.primarySymbol !== "—" ? c.primarySymbol : null)
            || (Array.isArray(c.symbols) && c.symbols[0]) || c.id || c.host || "feed";
          const cell = cellFor(sym);
          const px = Number(c.last_price ?? c.px ?? (cell && cell.price) ?? 0);
          const tps = Number(c.ticksPerSec || 0);
          const rate = Array.isArray(c.rate) && c.rate.length > 1 ? c.rate : null;
          const gaps = Number(c.gapsToday || 0);
          const stale = !!c.stale;
          return (
            <li key={c.id || i} className="m-mkt-row" style={stale ? { opacity: 0.55 } : undefined}>
              <div className="m-mkt-l">
                <div className="m-mkt-sym mono">{sym} <span className="muted">· {c.venue || "—"}</span></div>
                <div className="m-mkt-reg mono muted">
                  {(cell && cell.regime) || c.state || "—"}
                  {cell && cell.adx ? <> · ADX {cell.adx}</> : null}
                  {gaps > 0 && <span className="warn"> · {gaps} gap{gaps === 1 ? "" : "s"}</span>}
                  {stale && <span className="neg"> · STALE</span>}
                </div>
              </div>
              <div className="m-mkt-spark" style={{ color: stale ? "var(--muted)" : "var(--accent)" }}>
                {rate ? <MSpark data={rate.slice(-30)} h={28} /> : null}
              </div>
              <div className="m-mkt-r mono">
                <div className="m-mkt-px tnum">{px > 0 ? px.toLocaleString("en-US", { minimumFractionDigits: 2 }) : "—"}</div>
                <div className="m-mkt-chg tnum muted">{tps.toFixed(tps >= 10 ? 0 : 1)} tps</div>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="m-bottom-pad" />
    </>
  );
}

/* ───── Alerts screen ─────────────────────────────────────── */
const M_AL_SEV = {
  crit: { lbl: "CRIT", color: "var(--neg)" },
  warn: { lbl: "WARN", color: "var(--warn)" },
  info: { lbl: "INFO", color: "var(--muted-2)" },
};

function mFmtKey(k) { return String(k).replace(/_/g, " "); }
function mFmtVal(v) {
  if (v === null || v === undefined) return { text: "—", cls: "muted" };
  if (typeof v === "boolean") return { text: v ? "true" : "false", cls: v ? "pos" : "neg" };
  if (typeof v === "number") {
    if (Number.isInteger(v) && Math.abs(v) > 1e12) {
      try {
        const d = new Date(v / 1000);
        return { text: d.toISOString().replace("T", " ").replace(/\.\d+Z$/, "Z"), cls: "muted" };
      } catch { /* */ }
    }
    if (!Number.isInteger(v)) return { text: v.toLocaleString(undefined, { maximumFractionDigits: 6 }), cls: "" };
    return { text: v.toLocaleString(), cls: "" };
  }
  return { text: String(v), cls: "" };
}

function MAlertCard({ a, expanded, onToggle, onAck, onCopy, copied }) {
  const sev = M_AL_SEV[a.sev] || M_AL_SEV.info;
  const entries = Object.entries(a.payload || {});
  return (
    <article className={"m-al-card" + (expanded ? " open" : "")} data-sev={a.sev} data-ack={a.ack}>
      <button className="m-al-head" onClick={onToggle}>
        <span className="m-al-sev-dot" style={{ background: sev.color }} />
        <div className="m-al-head-body">
          <div className="m-al-head-meta mono">
            <span className="m-al-sev-lbl" style={{ color: sev.color }}>{sev.lbl}</span>
            <span className="m-al-sep">·</span>
            <span>{a.bucket}</span>
            {a.host && <>
              <span className="m-al-sep">·</span>
              <span>host <b>{a.host}</b></span>
            </>}
            {a.account && <>
              <span className="m-al-sep">·</span>
              <span>acct <b>{a.account}</b></span>
            </>}
            <span className="m-al-sep">·</span>
            <span className="muted">{a.age}</span>
          </div>
          <div className="m-al-head-title mono" title={a.msg}>{a.head}</div>
        </div>
        <span className="m-al-chev" aria-hidden>{expanded ? "▴" : "▾"}</span>
      </button>

      {expanded && (
        <div className="m-al-body">
          <p className="m-al-msg">{a.msg}</p>

          <div className="m-al-card-h">
            <span className="m-al-section-lbl mono">EVENT PAYLOAD</span>
            {a.payload?.kind && <span className="m-al-payload-kind mono">{a.payload.kind}</span>}
          </div>

          <div className="m-al-kv">
            {entries.map(([k, v]) => {
              const fmt = mFmtVal(v);
              return (
                <div className="m-al-kv-row" key={k}>
                  <div className="m-al-kv-k mono">{mFmtKey(k)}</div>
                  <div className={"m-al-kv-v mono " + (fmt.cls || "")}>{fmt.text}</div>
                </div>
              );
            })}
          </div>

          <div className="m-al-id mono muted">{a.id}</div>
        </div>
      )}

      <div className="m-al-actions">
        <button className="m-al-copy mono" onClick={onCopy} data-copied={copied}>
          {copied === "copied" ? "✓ copied" : copied === "error" ? "✕ failed" : "⧉ copy json"}
        </button>
        {!a.ack
          ? <button className="m-al-ack mono" onClick={(e) => { e.stopPropagation(); onAck(); }}>ack</button>
          : <span className="m-al-ack-tag mono">acked · {a.ackBy}</span>}
      </div>
    </article>
  );
}

/* ───── Notification settings + lock-screen demo ──────────── */

const NOTIF_DEFAULTS = {
  enabled: true,
  sound: true,
  tone: "chime",        // chime | beep | siren
  vibrate: true,
  lockscreen: true,
  sev: { crit: true, warn: true, info: false },
};

function loadNotifPrefs() {
  try {
    const raw = localStorage.getItem("aviaf:notif");
    if (!raw) return { ...NOTIF_DEFAULTS };
    const p = JSON.parse(raw);
    return { ...NOTIF_DEFAULTS, ...p, sev: { ...NOTIF_DEFAULTS.sev, ...(p.sev || {}) } };
  } catch (_) {
    return { ...NOTIF_DEFAULTS };
  }
}
function saveNotifPrefs(p) {
  try { localStorage.setItem("aviaf:notif", JSON.stringify(p)); } catch (_) {}
}

/* Web Audio tone generator */
function playTone(tone) {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ac = new AC();
    const now = ac.currentTime;
    const gain = ac.createGain();
    gain.connect(ac.destination);

    const playNote = (freq, start, dur, vol = 0.18, type = "sine") => {
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.type = type;
      o.frequency.setValueAtTime(freq, now + start);
      g.gain.setValueAtTime(0, now + start);
      g.gain.linearRampToValueAtTime(vol, now + start + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      o.connect(g); g.connect(gain);
      o.start(now + start);
      o.stop(now + start + dur + 0.02);
    };

    if (tone === "chime") {
      playNote(880, 0.00, 0.40);
      playNote(1175, 0.13, 0.55);
    } else if (tone === "beep") {
      playNote(1320, 0.00, 0.12, 0.22, "square");
      playNote(1320, 0.18, 0.12, 0.22, "square");
    } else if (tone === "siren") {
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.type = "sawtooth";
      o.frequency.setValueAtTime(440, now);
      o.frequency.linearRampToValueAtTime(820, now + 0.30);
      o.frequency.linearRampToValueAtTime(440, now + 0.60);
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.20, now + 0.02);
      g.gain.linearRampToValueAtTime(0.20, now + 0.55);
      g.gain.exponentialRampToValueAtTime(0.0001, now + 0.65);
      o.connect(g); g.connect(gain);
      o.start(now); o.stop(now + 0.7);
    }
    setTimeout(() => ac.close().catch(()=>{}), 1500);
  } catch (_) {}
}

function MNotifSettings({ onTest }) {
  const [prefs, setPrefs] = mState(loadNotifPrefs);
  const [open, setOpen] = mState(false);

  const update = (patch) => {
    setPrefs(prev => {
      const next = { ...prev, ...patch };
      if (patch.sev) next.sev = { ...prev.sev, ...patch.sev };
      saveNotifPrefs(next);
      return next;
    });
  };

  const summary = prefs.enabled
    ? [prefs.sound && "sound", prefs.vibrate && "vibrate", prefs.lockscreen && "lock-screen"].filter(Boolean).join(" · ") || "silent"
    : "off";

  const test = () => {
    if (prefs.sound) playTone(prefs.tone);
    if (prefs.vibrate && navigator.vibrate) navigator.vibrate([60, 80, 60, 80, 120]);
    if (onTest) onTest({ ...prefs });
  };

  return (
    <div className={"m-notif " + (open ? "open" : "")}>
      <button className="m-notif-head" onClick={() => setOpen(o => !o)}>
        <span className="m-notif-bell" data-on={prefs.enabled}>
          {prefs.enabled ? "🔔" : "🔕"}
        </span>
        <span className="m-notif-h-l">
          <span className="m-notif-h-t mono">Notifications</span>
          <span className="m-notif-h-s mono muted">{summary}</span>
        </span>
        <span className="m-notif-h-cnt mono">
          {Object.entries(prefs.sev).filter(([_, v]) => v).map(([k]) => (
            <span key={k} className={"m-notif-sev-tag " + k}>{k}</span>
          ))}
        </span>
        <span className="m-notif-chev">{open ? "▾" : "▸"}</span>
      </button>

      {open && (
        <div className="m-notif-body">
          <div className="m-notif-row">
            <span className="m-notif-lbl">Enable</span>
            <button className={"m-tog " + (prefs.enabled ? "on" : "")} onClick={() => update({ enabled: !prefs.enabled })}>
              <span className="m-tog-knob" />
            </button>
          </div>
          <div className="m-notif-row" data-disabled={!prefs.enabled}>
            <span className="m-notif-lbl">Sound</span>
            <button className={"m-tog " + (prefs.sound ? "on" : "")} onClick={() => update({ sound: !prefs.sound })}>
              <span className="m-tog-knob" />
            </button>
          </div>
          {prefs.sound && (
            <div className="m-notif-row m-notif-sub" data-disabled={!prefs.enabled}>
              <span className="m-notif-lbl">Tone</span>
              <div className="m-notif-tones">
                {["chime", "beep", "siren"].map(t => (
                  <button key={t} className="m-notif-tone mono" data-on={prefs.tone === t}
                    onClick={() => { update({ tone: t }); playTone(t); }}>{t}</button>
                ))}
              </div>
            </div>
          )}
          <div className="m-notif-row" data-disabled={!prefs.enabled}>
            <span className="m-notif-lbl">Vibrate{!navigator.vibrate ? <span className="muted mono" style={{fontSize:9, marginLeft:6}}> · unsupported</span> : null}</span>
            <button className={"m-tog " + (prefs.vibrate ? "on" : "")} onClick={() => update({ vibrate: !prefs.vibrate })}>
              <span className="m-tog-knob" />
            </button>
          </div>
          <div className="m-notif-row" data-disabled={!prefs.enabled}>
            <span className="m-notif-lbl">Lock-screen banner</span>
            <button className={"m-tog " + (prefs.lockscreen ? "on" : "")} onClick={() => update({ lockscreen: !prefs.lockscreen })}>
              <span className="m-tog-knob" />
            </button>
          </div>
          <div className="m-notif-row m-notif-sev-row" data-disabled={!prefs.enabled}>
            <span className="m-notif-lbl">Ring for</span>
            <div className="m-notif-sev-pills">
              {["crit", "warn", "info"].map(s => (
                <button key={s} className={"m-notif-sev-pill " + s} data-on={prefs.sev[s]}
                  onClick={() => update({ sev: { [s]: !prefs.sev[s] } })}>
                  <span className="d" />{s}
                </button>
              ))}
            </div>
          </div>
          <div className="m-notif-actions">
            <button className="m-notif-test mono" onClick={test} disabled={!prefs.enabled}>
              Ring test alert
            </button>
            <div className="m-notif-hint mono muted">
              Lock the phone before tapping to see the banner & feel vibration.
            </div>
          </div>
        </div>
      )}
    </div>);
}

/* Lock-screen banner overlay — visualises what a real iOS notification looks like */
function MLockScreenBanner({ alert, onDismiss }) {
  const [entering, setEntering] = mState(true);
  mEffect(() => {
    if (!alert) return;
    // animate in next frame
    const r1 = requestAnimationFrame(() => requestAnimationFrame(() => setEntering(false)));
    const t = setTimeout(() => onDismiss && onDismiss(), 4500);
    return () => { cancelAnimationFrame(r1); clearTimeout(t); };
  }, [alert]);
  if (!alert) return null;
  return (
    <div className={"m-lsbanner " + (entering ? "entering" : "")} data-sev={alert.sev || "info"} onClick={onDismiss}>
      <div className="m-lsbanner-icon">
        <span className="brand-mini">▣</span>
      </div>
      <div className="m-lsbanner-body">
        <div className="m-lsbanner-row">
          <span className="m-lsbanner-app mono">AVIAF · OPS</span>
          <span className="m-lsbanner-time mono muted">now</span>
        </div>
        <div className="m-lsbanner-title" title={alert.msg}>{alert.head}</div>
        <div className="m-lsbanner-msg mono">{alert.msg}</div>
      </div>
    </div>);
}

function MAlerts() {
  // Self-subscribing: normalize window.ALERTS on live-data bumps
  // (useSyncExternalStore) instead of the old MobileApp-owned list that a
  // 1-second setInterval re-diffed forever — that timer re-rendered the
  // whole app shell every second even when nothing changed.  Optimistic
  // ack state lives in a small per-id override map on top.
  const rev = window.useLive();
  const base = mMemo(
    () => (window.ALERTS || []).map((a) => window.normalizeAlertRow ? window.normalizeAlertRow(a) : a),
    [rev]);
  const [over, setOver] = mState({});
  const list = mMemo(
    () => base.map((a) => (over[a.id] ? { ...a, ...over[a.id] } : a)),
    [base, over]);
  const [query, setQuery] = mState("");
  const [demoBanner, setDemoBanner] = mState(null);
  const [filters, setFilters] = mState({
    status: "open",
    sev: new Set(["info", "warn", "crit"]),
    source: new Set(["risk", "ingest", "system", "research"]),
  });
  const [expanded, setExpanded] = mState(null);
  const [copied, setCopied] = mState({ id: null, state: null });

  const toggleSet = (k, v) => setFilters(f => {
    const s = new Set(f[k]);
    if (s.has(v)) s.delete(v); else s.add(v);
    return { ...f, [k]: s };
  });

  const filtered = mMemo(() => {
    const q = query.trim().toLowerCase();
    return list.filter(a => {
      if (filters.status === "open" && a.ack) return false;
      if (filters.status === "ack" && !a.ack) return false;
      if (!filters.sev.has(a.sev)) return false;
      if (!filters.source.has(a.bucket)) return false;
      if (!q) return true;
      const hay = [
        a.id, a.head, a.msg, a.bucket, a.account,
        ...Object.entries(a.payload || {}).map(([k, v]) => k + ":" + JSON.stringify(v)),
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [list, filters, query]);

  // Optimistic ack — flip the override immediately, then POST.  On failure
  // drop the override so the row reverts.  alert_pk is the canonical DB key;
  // synthetic negative pks (WS-pushed before backfill) are skipped.
  const ack = async (id) => {
    const target = list.find(a => a.id === id);
    if (!target || !target.alert_pk || target.alert_pk < 0) return;
    setOver(o => ({ ...o, [id]: { ack: true, ackBy: "you" } }));
    try {
      const r = await fetch(`/api/alerts/${target.alert_pk}/ack`, { method: "POST", credentials: "same-origin" });
      if (!r.ok) throw new Error(`ack ${r.status}`);
    } catch (e) {
      setOver(o => { const n = { ...o }; delete n[id]; return n; });
      console.error("[mobile alerts] ack failed", e);
    }
  };
  const copyJson = (a) => {
    const text = JSON.stringify(a.payload, null, 2);
    const setOK = (st) => { setCopied({ id: a.id, state: st }); setTimeout(() => setCopied({ id: null, state: null }), 1500); };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => setOK("copied"), () => setOK("error"));
    } else {
      try {
        const ta = document.createElement("textarea");
        ta.value = text; document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
        setOK("copied");
      } catch { setOK("error"); }
    }
  };

  const openCount = list.filter(a => !a.ack).length;
  const ackCount  = list.filter(a => a.ack).length;
  const critOpen  = list.filter(a => !a.ack && a.sev === "crit").length;

  return (
    <>
      <MNotifSettings onTest={(prefs) => {
        if (!prefs.lockscreen) return;
        const top = list.filter(a => !a.ack).sort((a, b) =>
          ({ crit: 3, warn: 2, info: 1 }[b.sev] - { crit: 3, warn: 2, info: 1 }[a.sev]))[0] || list[0];
        if (top) setDemoBanner(top);
      }} />
      <MLockScreenBanner alert={demoBanner} onDismiss={() => setDemoBanner(null)} />

      <div className="m-al-summary">
        <div className="m-al-summary-l">
          <div className="m-al-summary-num-wrap">
            <span className="m-al-summary-num mono tnum">{openCount}</span>
            <span className="m-al-summary-num-l mono muted">open · {ackCount} acked</span>
          </div>
          {critOpen > 0 && (
            <div className="m-al-summary-crit mono">
              <span className="m-al-summary-crit-dot" />
              {critOpen} crit
            </div>
          )}
        </div>
        {list.some(a => !a.ack) && (
          <button className="m-btn m-al-ack-all" onClick={async () => {
            if (!window.confirm(`Acknowledge all ${openCount} open alert${openCount === 1 ? "" : "s"}?`)) return;
            try {
              // One bulk request instead of a POST per row (was sequential
              // over the filtered set; slow with hundreds of alerts).
              await window.ackAllAlerts(false);
              setOver(o => {
                const n = { ...o };
                for (const a of list) if (!a.ack) n[a.id] = { ack: true, ackBy: "you" };
                return n;
              });
            } catch (e) { console.error("[mobile alerts] ack-all failed", e); }
          }}>
            Ack all
          </button>
        )}
      </div>

      <div className="m-al-search">
        <span className="m-al-search-icon" aria-hidden>⌕</span>
        <input
          type="text"
          className="m-al-search-input mono"
          placeholder="search id, source, account, payload…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
        />
        {query && (
          <button className="m-al-search-clear" onClick={() => setQuery("")} aria-label="clear">✕</button>
        )}
      </div>

      <div className="m-al-chips">
        <div className="m-al-chips-grp">
          {["all", "open", "ack"].map(s => (
            <button key={s} className="m-al-chip mono" data-on={filters.status === s}
              onClick={() => setFilters(f => ({ ...f, status: s }))}>{s}</button>
          ))}
        </div>
        <div className="m-al-chips-grp">
          {["info", "warn", "crit"].map(s => (
            <button key={s} className={"m-al-chip mono m-al-chip-sev-" + s} data-on={filters.sev.has(s)}
              onClick={() => toggleSet("sev", s)}>{s}</button>
          ))}
        </div>
        <div className="m-al-chips-grp">
          {["risk", "ingest", "system", "research"].map(s => (
            <button key={s} className="m-al-chip mono" data-on={filters.source.has(s)}
              onClick={() => toggleSet("source", s)}>{s}</button>
          ))}
        </div>
      </div>

      <div className="m-al-count mono muted">
        {filtered.length} {filtered.length === 1 ? "alert" : "alerts"}
        {query && <> matching “{query}”</>}
      </div>

      {filtered.length === 0 && (
        <div className="m-al-empty">
          <div className="m-al-empty-h mono">no alerts match</div>
          <div className="m-al-empty-s mono">adjust the chips above or clear search.</div>
        </div>
      )}

      <ul className="m-al-list">
        {filtered.map(a => (
          <li key={a.id}>
            <MAlertCard
              a={a}
              expanded={expanded === a.id}
              onToggle={() => setExpanded(e => e === a.id ? null : a.id)}
              onAck={() => ack(a.id)}
              onCopy={(e) => { e.stopPropagation?.(); copyJson(a); }}
              copied={copied.id === a.id ? copied.state : null}
            />
          </li>
        ))}
      </ul>

      <div className="m-bottom-pad" />
    </>
  );
}

/* ───── Activity screen ───────────────────────────────────── */
function MActivity() {
  const [filter, setFilter] = mState("all");
  // The chips previously computed a filtered list that was never passed to
  // <MFeed /> — tapping FILLS/RISK/SYS did nothing.  MFeed filters now.
  return (
    <>
      <div className="m-segctl mono">
        {[["all","ALL"],["fill","FILLS"],["signal","SIGNALS"],["risk","RISK"],["sys","SYS"]].map(([k, l]) => (
          <button key={k} data-active={filter === k} onClick={() => setFilter(k)}>{l}</button>
        ))}
      </div>
      <MFeed tag={filter} />
      <div className="m-bottom-pad" />
    </>
  );
}

/* ───── Drawer ────────────────────────────────────────────── */
function drawerSubFor(k) {
  // Every sub-line is live-derived.  The old strings hardcoded a broker
  // name, "1 host" and a fabricated "25.2k tps".
  if (k === "overview") return "dashboard · live";
  if (k === "accounts") {
    const all = window.ACCOUNTS || [];
    const ext = all.filter(mIsExternalAcct).length;
    return (all.length - ext) + " active" + (ext ? ` · +${ext} mirrored` : "");
  }
  if (k === "strategies") {
    const own = mOwnStrats().filter(s => s.state !== "stopped" && !s.retired);
    const live = own.filter(s => s.state === "running" || s.state === "armed").length;
    return live + "/" + own.length + " live · " + (window.STRATEGY_DEFS || []).length + " defs";
  }
  if (k === "market") {
    const cols = window.COLLECTORS || [];
    const tps = (window.STORAGE_ROLLUP && window.STORAGE_ROLLUP.tps) || 0;
    return cols.length + " feed" + (cols.length === 1 ? "" : "s") + (tps > 0 ? ` · ${tps >= 1000 ? (tps / 1000).toFixed(1) + "k" : Math.round(tps)} tps` : "");
  }
  if (k === "alerts") {
    const open = (window.ALERTS || []).filter(a => !mAlAck(a)).length;
    const crit = (window.ALERTS || []).filter(a => !mAlAck(a) && mAlSev(a) === "crit").length;
    return open + " open" + (crit > 0 ? " · " + crit + " crit" : "");
  }
  if (k === "ops") {
    const S = window.OpsStore;
    if (!S) return "decision queue · incidents";
    const q = S.actions.filter(r => r.status === "proposed").length;
    const inc = S.incidents.filter(i => i.status === "open").length;
    return q + " decision" + (q === 1 ? "" : "s") + " · " + inc + " open incident" + (inc === 1 ? "" : "s");
  }
  if (k === "activity") return (window.FEED || []).length + " recent events";
  if (k === "instances") {
    const insts = window.INSTANCES || [];
    const up = insts.filter(i => i.state === "running").length;
    return up + "/" + insts.length + " agents up";
  }
  if (k === "collectors") return drawerSubFor("market");
  if (k === "research") {
    const n = (window.RESEARCH_CANDIDATES || []).length + (window.RESEARCH || []).length;
    return n + " artifact" + (n === 1 ? "" : "s");
  }
  if (k === "configs") return (window.CONFIGS || []).length + " configs";
  return "";
}

const M_DRAWER_NAV = [
  ["overview",   "Overview",   "home"],
  ["accounts",   "Accounts",   "acct"],
  ["strategies", "Strategies", "strat"],
  ["market",     "Market",     "mkt"],
  ["alerts",     "Alerts",     "al"],
  ["ops",        "Ops",        "ops"],
  ["activity",   "Activity",   "act"],
  ["instances",  "Instances",  "proc"],
  ["collectors", "Collectors", "col"],
  ["research",   "Research",   "res"],
  ["configs",    "Configs",    "cfg"],
];

function MDrawer({ open, onClose, theme, setTheme, onNav, current, session, onLogout, drawerRef, scrimRef }) {
  const { loading } = useMLoaded();
  const accts = window.ACCOUNTS || [];
  const verified = accts.filter((a) => a.equityVerified);
  const totalEq = verified.reduce((s, a) => s + (a.equity || 0), 0);
  const dayPnl = verified.reduce((s, a) => s + (a.dayPnl || 0), 0);
  const eqLoading = loading("equitySnapshot") || verified.length === 0;
  const subj = (session && session.subject) || "operator";
  return (
    <>
      <div ref={scrimRef} className={"m-drawer-scrim" + (open ? " open" : "")} onClick={onClose} />
      <aside ref={drawerRef} className={"m-drawer" + (open ? " open" : "")}>
        <div className="m-drawer-user">
          <div className="m-avatar">{(subj || "x").slice(0, 1)}</div>
          <div className="m-drawer-user-l">
            <div className="m-drawer-name mono">{subj}</div>
            <div className="m-drawer-role mono muted">{session && session.auth_enabled === false ? "auth disabled" : "logged in"}</div>
          </div>
          <button className="m-icon-btn" onClick={onClose} aria-label="close">
            <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="m-drawer-summary">
          <div className="m-drawer-sum-block">
            <div className="mono muted m-drawer-sum-l">EQUITY</div>
            <div className="mono tnum m-drawer-sum-v">
              {eqLoading ? <MSkel w={108} h={16} /> : window.fmt$(totalEq, 0)}
            </div>
          </div>
          <div className="m-drawer-sum-block">
            <div className="mono muted m-drawer-sum-l">DAY P&L</div>
            <div className={"mono tnum m-drawer-sum-v " + (dayPnl >= 0 ? "pos" : "neg")}>
              {eqLoading
                ? <MSkel w={84} h={16} />
                : <>{dayPnl >= 0 ? "+" : ""}{window.fmt$(dayPnl, 0)}</>}
            </div>
          </div>
        </div>

        <nav className="m-drawer-nav">
          {M_DRAWER_NAV.map(([k, l]) => (
            <button key={k} className={"m-drawer-link" + (current === k ? " active" : "")}
              onClick={() => onNav(k)}>
              <span className="m-drawer-link-l">{l}</span>
              <span className="m-drawer-link-s mono muted">{drawerSubFor(k)}</span>
              <span className="m-drawer-arrow">›</span>
            </button>
          ))}
        </nav>

        <div className="m-drawer-theme">
          <div className="m-drawer-theme-l mono muted">SURFACE</div>
          <div className="m-segctl mono compact">
            {[["paper","☼ paper"],["dark","☾ dark"],["terminal","▮ term"]].map(([k, l]) => (
              <button key={k} data-active={theme === k} onClick={() => setTheme(k)}>{l}</button>
            ))}
          </div>
        </div>

        <div className="m-drawer-foot">
          <button className="m-btn m-btn-block" onClick={() => { if (onLogout) onLogout(); }}>→ Logout</button>
          <div className="m-drawer-build mono muted">aviaf · v0.7.2</div>
        </div>
      </aside>
    </>
  );
}

/* ───── Bottom nav ────────────────────────────────────────── */
const M_TABS = [
  { k: "overview",   l: "Home",     icon: (
      <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
        <rect x="3" y="3" width="6" height="6" stroke="currentColor" strokeWidth="1.3"/>
        <rect x="11" y="3" width="6" height="6" stroke="currentColor" strokeWidth="1.3"/>
        <rect x="3" y="11" width="6" height="6" stroke="currentColor" strokeWidth="1.3"/>
        <rect x="11" y="11" width="6" height="6" stroke="currentColor" strokeWidth="1.3"/>
      </svg>
  )},
  { k: "alerts",     l: "Alerts", icon: (
      <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
        <path d="M10 2.5v1.4M4 14V9a6 6 0 1112 0v5l1.5 1.6H2.5L4 14zm3.5 2.4a2.5 2.5 0 005 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      </svg>
  )},
  { k: "ops",        l: "Ops", icon: (
      <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
        <path d="M3 5.5h14M3 10h14M3 14.5h14" stroke="currentColor" strokeWidth="1.3"/>
        <circle cx="7.5" cy="5.5" r="2" fill="var(--paper)" stroke="currentColor" strokeWidth="1.3"/>
        <circle cx="12.5" cy="10" r="2" fill="var(--paper)" stroke="currentColor" strokeWidth="1.3"/>
        <circle cx="6.5" cy="14.5" r="2" fill="var(--paper)" stroke="currentColor" strokeWidth="1.3"/>
      </svg>
  )},
  { k: "research",   l: "Research", icon: (
      <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
        <path d="M8 2.5h4M9 2.5v5.2L4.6 15a1.6 1.6 0 001.4 2.4h8a1.6 1.6 0 001.4-2.4L11 7.7V2.5" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" fill="none"/>
        <path d="M6.5 12.5h7" stroke="currentColor" strokeWidth="1.3"/>
      </svg>
  )},
  { k: "accounts",   l: "Accounts", icon: (
      <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
        <rect x="2.5" y="5" width="15" height="11" stroke="currentColor" strokeWidth="1.3"/>
        <path d="M2.5 9h15M6 13h3" stroke="currentColor" strokeWidth="1.3"/>
      </svg>
  )},
];

function MBottomNav({ tab, setTab }) {
  return (
    <nav className="m-bottom-nav">
      {M_TABS.map(t => (
        <button key={t.k} className={"m-tab" + (tab === t.k ? " active" : "")} onClick={() => setTab(t.k)}>
          <span className="m-tab-icon">{t.icon}</span>
          <span className="m-tab-l mono">{t.l}</span>
        </button>
      ))}
    </nav>
  );
}

/* ───── FAB ────────────────────────────────────────────────── */
function MFab() {
  const [open, setOpen] = mState(false);
  return (
    <>
      <button className={"m-fab" + (open ? " open" : "")} onClick={() => setOpen(o => !o)} aria-label="quick actions">
        {open ? (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M5 5l10 10M15 5l-10 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 2v16M2 10h16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
        )}
      </button>
      {open && (
        <div className="m-fab-menu">
          <button className="m-fab-item" onClick={() => { setOpen(false); if (typeof window.refreshBootstrap === "function") window.refreshBootstrap(); }}>
            <span className="m-fab-icon">⊙</span><span>Refresh data</span>
          </button>
        </div>
      )}
    </>
  );
}

/* ───── Pull-to-refresh handler ────────────────────────────── */
/* Fully imperative: the indicator element is grown/labelled via direct
 * DOM writes during the gesture and React state never changes per-frame.
 * The old version called setPull() on every touchmove — re-rendering the
 * ENTIRE app tree per frame — and, because the effect depended on `pull`,
 * tore down and re-attached the touch listeners mid-gesture (dropped
 * events → the "glitchy" pull).  Listeners now attach exactly once. */
function usePullRefresh(scrollRef, indRef, onRefresh) {
  const cbRef = mRef(onRefresh);
  cbRef.current = onRefresh;
  mEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let start = null;
    let pull = 0;
    const setPull = (px) => {
      pull = px;
      const n = indRef.current;
      if (!n) return;
      n.style.height = px > 0 ? (px * 0.6).toFixed(1) + "px" : "0px";
      n.textContent = px > 0 ? (px > 50 ? "↻ release to refresh" : "↓ pull to refresh") : "";
    };
    const ts = (e) => {
      if (el.scrollTop <= 0) start = e.touches[0].clientY;
    };
    const tm = (e) => {
      if (start == null) return;
      const dy = e.touches[0].clientY - start;
      if (dy > 0 && el.scrollTop <= 0) {
        e.preventDefault();
        setPull(Math.min(90, dy * 0.5));
      } else if (pull !== 0) {
        setPull(0);
      }
    };
    const te = () => {
      const fire = pull > 50;
      start = null;
      setPull(0);
      if (fire && cbRef.current) cbRef.current();
    };
    el.addEventListener("touchstart", ts, { passive: true });
    el.addEventListener("touchmove", tm, { passive: false });
    el.addEventListener("touchend", te);
    el.addEventListener("touchcancel", te);
    return () => {
      el.removeEventListener("touchstart", ts);
      el.removeEventListener("touchmove", tm);
      el.removeEventListener("touchend", te);
      el.removeEventListener("touchcancel", te);
    };
  }, []);
}

/* ───── Account detail (deep-link target for card "Open") ───── */
function MAccountDetail({ acctId }) {
  window.useLive();
  const accts = window.ACCOUNTS || [];
  const a = accts.find((x) => x.id === acctId) || accts.find((x) => x.label === acctId);
  if (!a) return <MStubScreen tab={"account: " + acctId} />;
  const strats = (window.STRATS || []).filter(
    (s) => s.acct === a.id && (!a.instance || s.instance === a.instance));
  return (
    <>
      <MAccountCard a={a} defaultOpen={true} />
      <MGenericList items={strats} empty="No active strategies on this account."
        renderRow={(s) => (
          <a key={s.id} className="m-list-row" href={`#/strategies/${encodeURIComponent(s.defId || s.id)}`}>
            <div className="m-list-row-main mono">{s.name}</div>
            <div className="m-list-row-sub mono muted">
              {(s.state || "—")} · WR {Number.isFinite(s.wr) ? (s.wr * 100).toFixed(0) + "%" : "—"}
              {" "}· PF {Number.isFinite(s.pf) ? s.pf.toFixed(2) : "—"} · {window.fmt$(s.pnl30, 0)} 30d
            </div>
          </a>)}
      />
    </>
  );
}

/* ───── Instance detail (deep-link target for card "Logs") ──── */
function MInstanceDetail({ instId }) {
  window.useLive();
  const insts = window.INSTANCES || [];
  const i = insts.find((x) => x.id === instId);
  if (!i) return <MStubScreen tab={"instance: " + instId} />;
  const strats = (window.STRATS || []).filter((s) => s.instance === i.id);
  const info = [
    ["host", i.host || i.id],
    ["state", i.state || "—"],
    ["version", i.version || i.py || "?"],
    ["strategies", String(strats.length)],
    ["uptime", i.uptime || "—"],
  ];
  return (
    <>
      <MGenericList items={info} empty=""
        renderRow={([k, v]) => (
          <div key={k} className="m-list-row">
            <div className="m-list-row-main mono muted">{k}</div>
            <div className="m-list-row-sub mono">{v}</div>
          </div>)}
      />
      <MGenericList items={strats} empty="No strategies on this instance."
        renderRow={(s) => (
          <a key={s.id} className="m-list-row" href={`#/accounts/${encodeURIComponent(s.acct)}`}>
            <div className="m-list-row-main mono">{s.name}</div>
            <div className="m-list-row-sub mono muted">{s.acct} · {(s.state || "—")} · {window.fmt$(s.pnl30, 0)} 30d</div>
          </a>)}
      />
    </>
  );
}

/* ───── App ───────────────────────────────────────────────── */
function MobileApp({ session, onLogout, tweaks, setTweak }) {
  // Parse BOTH hash segments so detail deep-links (#/accounts/{id},
  // #/instances/{id}) resolve to real views, not the bare list screen.
  const parseHash = () => {
    const parts = (location.hash || "#/overview").replace(/^#\/?/, "").split("/");
    return { top: parts[0] || "overview", sub: parts[1] ? decodeURIComponent(parts[1]) : null };
  };
  const [tab, setTab] = mState(() => parseHash().top);
  const [sub, setSub] = mState(() => parseHash().sub);
  const [drawer, setDrawer] = mState(false);
  const [theme, setThemeLocal] = mState((tweaks && tweaks.theme) || "paper");
  const setTheme = (t) => { setThemeLocal(t); if (setTweak) setTweak("theme", t); };
  const [refreshing, setRefreshing] = mState(false);
  const scrollRef = mRef(null);
  const ptrRef = mRef(null);
  const swipeRef = mRef(null);
  const drawerRef = mRef(null);
  const scrimRef = mRef(null);

  // NOTE: the app shell deliberately owns NO ticking state.  The clock,
  // alert badge, refresh bar and every data card subscribe themselves to
  // live-data bumps — the shell re-renders only on navigation / drawer /
  // theme / refresh-state changes.  (Previously a 1 Hz clock + a 1 s alert
  // re-normalize interval lived here and re-rendered the whole tree.)

  // pull-to-refresh — actually re-fetch (window.refreshBootstrap forces a
  // full bootstrap past the 5s coalesce window); spinner clears on resolve.
  usePullRefresh(scrollRef, ptrRef, () => {
    setRefreshing(true);
    Promise.resolve(
      typeof window.refreshBootstrap === "function" ? window.refreshBootstrap() : null
    ).catch(() => {}).finally(() => setRefreshing(false));
  });

  // Edge-swipe drawer: transforms are written STRAIGHT to the drawer/scrim
  // DOM nodes during the gesture (no per-frame setState, no per-frame
  // <style> tag injection — the old approach re-rendered the entire app and
  // forced a full-page style recalc on every touchmove frame).
  const setDrawerTx = (tx) => {
    const d = drawerRef.current, s = scrimRef.current;
    if (d) { d.style.transition = "none"; d.style.transform = `translateX(${tx - 320}px)`; }
    if (s) { s.style.transition = "none"; s.style.opacity = String(tx / 320); s.style.pointerEvents = tx > 20 ? "auto" : "none"; }
  };
  const clearDrawerTx = () => {
    for (const n of [drawerRef.current, scrimRef.current]) {
      if (!n) continue;
      n.style.transition = ""; n.style.transform = ""; n.style.opacity = ""; n.style.pointerEvents = "";
    }
  };
  const onTouchStart = (e) => {
    const t = e.touches[0];
    const fromEdge = !drawer && t.clientX < 22;
    const fromDrawer = drawer && t.clientX < 320;
    if (!fromEdge && !fromDrawer) return;
    swipeRef.current = { x0: t.clientX, y0: t.clientY, dx: 0, kind: drawer ? "close" : "open" };
  };
  const onTouchMove = (e) => {
    if (!swipeRef.current) return;
    const t = e.touches[0];
    const dx = t.clientX - swipeRef.current.x0;
    const dy = t.clientY - swipeRef.current.y0;
    if (!swipeRef.current.committed) {
      if (Math.abs(dy) > Math.abs(dx) * 1.4 && Math.abs(dy) > 10) {
        swipeRef.current = null;
        return;
      }
      if (Math.abs(dx) > 8) swipeRef.current.committed = true;
    }
    swipeRef.current.dx = dx;
    if (!swipeRef.current.committed) return;
    setDrawerTx(swipeRef.current.kind === "open"
      ? Math.max(0, Math.min(320, dx))
      : Math.max(0, Math.min(320, 320 + dx)));
  };
  const onTouchEnd = () => {
    const s = swipeRef.current;
    if (!s) return;
    clearDrawerTx();
    if (s.kind === "open" && s.dx > 80) setDrawer(true);
    else if (s.kind === "close" && s.dx < -80) setDrawer(false);
    swipeRef.current = null;
  };

  const goto = (k) => {
    setTab(k);
    setSub(null);
    setDrawer(false);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    if (location.hash !== "#/" + k) location.hash = "#/" + k;
  };

  // Sync tab with hash so deep-links land on the right page.
  mEffect(() => {
    const on = () => {
      const { top, sub } = parseHash();
      setTab(top);
      setSub(sub);
    };
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);

  const tabTitleMap = {
    overview: null, accounts: "ACCOUNTS", strategies: "STRATEGIES",
    market: "MARKET", activity: "ACTIVITY",
    alerts: "ALERTS", ops: "OPS",
    instances: "INSTANCES", collectors: "COLLECTORS",
    research: "RESEARCH", configs: "CONFIGS",
  };
  const screenTitle = sub ? sub : tabTitleMap[tab];

  let body;
  // Detail screens (second hash segment present) take precedence so the
  // card Open/Logs/history deep-links resolve to a real view, not the list.
  if (sub && tab === "accounts")        body = <MAccountDetail acctId={sub} />;
  else if (sub && tab === "instances")  body = <MInstanceDetail instId={sub} />;
  else if (tab === "overview")   body = <MOverview goto={goto} />;
  else if (tab === "accounts")   body = <MAccounts />;
  else if (tab === "strategies") body = <MStrategies />;
  else if (tab === "market")     body = <MMarket />;
  else if (tab === "activity")   body = <MActivity />;
  else if (tab === "alerts")     body = window.MScreenAlerts2 ? <div className="ops-console m-ops"><window.MScreenAlerts2 /></div> : <MAlerts />;
  else if (tab === "ops")        body = window.MScreenOps ? <div className="ops-console m-ops"><window.MScreenOps /></div> : <MStubScreen tab="ops" />;
  else if (tab === "instances")  body = <MGenericList source={() => window.INSTANCES} empty="No instances reporting yet."
    renderRow={(i) => (
      <a key={i.id} className="m-list-row" href={`#/instances/${encodeURIComponent(i.id)}`}>
        <div className="m-list-row-main mono">{i.host || i.id}</div>
        <div className="m-list-row-sub mono muted">{(i.state || "—")} · {i.strategies?.length || 0} strats · py {i.py || "?"}</div>
      </a>)}
  />;
  else if (tab === "collectors") body = <MGenericList source={() => window.COLLECTORS} empty="No collectors registered."
    renderRow={(c) => (
      <a key={c.id} className="m-list-row" href={`#/collectors/${encodeURIComponent(c.id)}`}>
        <div className="m-list-row-main mono">{c.label || c.id}</div>
        <div className="m-list-row-sub mono muted">{c.state || "—"} · {(c.ticksPerSec || 0).toFixed(0)} tps</div>
      </a>)}
  />;
  else if (tab === "configs")    body = <MGenericList source={() => window.CONFIGS} empty="No configs available."
    renderRow={(c) => (
      <a key={c.id} className="m-list-row" href={`#/configs/${encodeURIComponent(c.id)}`}>
        <div className="m-list-row-main mono">{c.label || c.file}</div>
        <div className="m-list-row-sub mono muted">{c.note || ""}</div>
      </a>)}
  />;
  else if (tab === "research")   body = window.MScreenResearch
    ? <div className="ops-console m-ops"><window.MScreenResearch /></div>
    : <MGenericList
        source={() => [...(window.RESEARCH_CANDIDATES || []), ...(window.RESEARCH || [])]}
        empty="No research artifacts yet."
        renderRow={(r) => (
          <a key={r.id || r.candidate_id} className="m-list-row" href={`#/research`}>
            <div className="m-list-row-main mono">{r.title || r.name || r.id || r.candidate_id}</div>
            <div className="m-list-row-sub mono muted">{r.status || r.tag || r.kind || ""}</div>
          </a>)}
      />;
  else body = <MStubScreen tab={tab} />;

  return (
    <div className="m-app" data-theme={theme}
         onTouchStart={onTouchStart}
         onTouchMove={onTouchMove}
         onTouchEnd={onTouchEnd}>
      <MHeader
        onMenu={() => setDrawer(true)}
        refreshing={refreshing}
        openAlerts={() => goto("alerts")}
      />
      {window.MCritStrip && <window.MCritStrip goto={goto} />}
      <div ref={scrollRef} className="m-scroll">
        <div ref={ptrRef} className="m-ptr mono" style={{ height: 0 }} />
        {refreshing && <div className="m-ptr-refreshing mono">refreshing…</div>}
        {screenTitle && (sub
          ? <button className="m-screen-title mono" onClick={() => goto(tab)}
              style={{ background: "none", border: "none", padding: 0, textAlign: "left", cursor: "pointer", color: "inherit", font: "inherit" }}>
              ‹ {screenTitle}
            </button>
          : <div className="m-screen-title mono">{screenTitle}</div>)}
        {body}
      </div>
      {window.ConnectionBanner && <window.ConnectionBanner />}
      <MFab />
      <MBottomNav tab={tab} setTab={(k) => {
        if (k === tab && scrollRef.current) scrollRef.current.scrollTop = 0;
        goto(k);
      }} />
      {window.MIncidentSheet && <window.MIncidentSheet />}
      <MDrawer open={drawer} onClose={() => setDrawer(false)}
        theme={theme} setTheme={setTheme}
        onNav={goto} current={tab}
        session={session} onLogout={onLogout}
        drawerRef={drawerRef} scrimRef={scrimRef} />
    </div>
  );
}

function MGenericList({ items, source, renderRow, empty }) {
  // Subscribe to live bumps and re-read via `source()` each render: these
  // lists show window.* arrays that data-live.jsx REPLACES wholesale on
  // updates, so a captured `items` array reference goes stale the moment
  // the shell stops re-rendering on a timer.
  window.useLive();
  const rows = typeof source === "function" ? (source() || []) : (items || []);
  if (rows.length === 0) {
    return (
      <div className="m-empty mono muted" style={{ padding: "20px 18px", textAlign: "center" }}>
        {empty || "—"}
      </div>
    );
  }
  return <div className="m-list">{rows.map(renderRow)}</div>;
}

function MStubScreen({ tab }) {
  return (
    <div style={{ padding: "32px 18px", textAlign: "center", color: "var(--muted)", fontFamily: "var(--mono)" }}>
      <div style={{ fontSize: 12, marginBottom: 8 }}>– {tab.toUpperCase()} –</div>
      <div style={{ fontSize: 11, opacity: 0.7 }}>Screen scaffolded · drill into desktop for full UX</div>
    </div>
  );
}

/* Export the helpers mobile-ops.jsx consumes at render (all guarded there):
 * MSpark (loop-lag + candidate sparklines), MNotifSettings (alerts screen),
 * MSection/MLockScreenBanner for completeness. */
Object.assign(window, { MobileApp, MSpark, MNotifSettings, MSection, MLockScreenBanner });
